// ACTA_PROSPECT_AFFAIRES_ONLY_V18
// ACTA_PROSPECT_VISUAL_COMPACT_V1
// ACTA_PROSPECT_DETAIL_CLEAN_COMMERCIAL_V1
// ACTA_PROSPECT_DETAIL_HORIZONTAL_TABS_V1
// ACTA_PROSPECT_DETAIL_PROFILE_HEADER_V1
// ACTA_PROSPECT_DETAIL_TRANSVERSAL_UI_V1
// ACTA_PROSPECT_ACTIVITY_DOCUMENTS_FUSION_V1
// ACTA_PROSPECT_EXPLICIT_HISTORY_V1
// ACTA_PROSPECT_HISTORY_ACTIONS_V1
// ACTA_HISTORY_SHARE_STATS_NAVIGATION_V1
// ACTA_PROSPECT_ASSIGNMENT_DOUBLE_CONFIRM_V1
// ACTA_PROSPECT_COMPACT_OPPORTUNITIES_V1
// acta_patch_lot3_5_machine_installation_v1
// acta_patch_lot3_4_purchase_orders_v1
// acta_patch_lot3_3_quote_lines_pdf_send_v1
// acta_patch_lot3_quote_from_prospect_v1
// acta_patch_machine_catalog_settings_tabs_remove_price_v1
// acta_patch_lot2_clients_machine_catalog_v1
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { formatApiError } from "@/lib/api";
import { whatsappUrl } from "@/lib/contactChannels";
import { buildMailComposePath } from "@/lib/mailCompose";
import {
  buildMeetingInvitationShareLinks,
  buildMeetingCalendarLinks,
  buildProspectMeetingAddress,
} from "@/lib/meetingShareLinks";
import useSpeechDictation from "@/hooks/useSpeechDictation";
import {
  appointments as appointmentsApi,
  prospects as prospectsApi,
  documents as documentsApi,
  shares as sharesApi,
  machineCatalog as machineCatalogApi,
  quotes as quotesApi,
} from "@/api";
import { toast } from "sonner";
import {
  Phone,
  EnvelopeSimple,
  MapPin,
  FileText,
  Clock,
  ShareNetwork,
  Buildings,
  PencilSimple,
  Trash,
  Note,
  Calendar,
  ChatCircleText,
  PaperPlaneTilt,
  Plus,
  Handshake,
  Sparkle,
  Lock,
  ArrowsClockwise,
  EnvelopeOpen,
  Phone as PhoneIcon,
  Gauge,
  Wrench,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import ShareModal from "@/components/ShareModal";
import ProspectOpportunityPanel from "@/components/ProspectOpportunityPanel";
import QuoteCreateForm from "@/components/QuoteCreateForm";
import OpportunityCreateModal from "@/components/OpportunityCreateModal";
import ContactInformationPanel from "@/components/ContactInformationPanel";
import ContactCallChannelSheet from "@/components/commerce/ContactCallChannelSheet";
import ContactFormFields, {
  getContactFormValidationError,
} from "@/components/commerce/ContactFormFields";
import CallReturnDialog from "@/components/commerce/CallReturnDialog";
import usePendingCallSession from "@/hooks/usePendingCallSession";
import QuotePdfPreviewModal from "@/components/commerce/QuotePdfPreviewModal";
import ContactDeleteConfirmModal from "@/components/commerce/ContactDeleteConfirmModal";
import ContactWorkshopsPanel from "@/components/commerce/ContactWorkshopsPanel";
import ContactMachinesPanel from "@/components/commerce/ContactMachinesPanel";
import PurchaseIntentBadge, {
  PURCHASE_INTENT_OPTIONS,
  normalizePurchaseIntent,
} from "@/components/commerce/PurchaseIntentBadge";
import DocumentPreviewModal from "@/components/documents/DocumentPreviewModal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * Taille de pagination interne sur les blocs de la fiche prospect.
 */
const PAGE_SIZE = 5;

/**
 * Libellés des statuts commerciaux.
 */
const STATUS_LABEL = {
  lead: "Lead",
  opportunity: "Opportunité",
  proposal: "Proposition",
  won: "Gagné",
  lost: "Perdu",
};

/**
 * Métadonnées d'activité utilisées pour afficher notes et timeline.
 */
const ACTIVITY_TYPES = {
  call: { label: "Appel", icon: PhoneIcon },
  email: { label: "Email", icon: EnvelopeOpen },
  email_sent: { label: "E-mail envoyé", icon: EnvelopeOpen },
  email_received: { label: "E-mail reçu", icon: EnvelopeOpen },
  meeting: { label: "Rendez-vous", icon: Handshake },
  followup: { label: "Relance", icon: ArrowsClockwise },
  note: { label: "Note libre", icon: Note },
};

const OUTCOME_LABELS = {
  successful: "Réussi",
  no_answer: "Sans réponse",
  rescheduled: "Reporté",
  cancelled: "Annulé",
  other: "Autre",
};

const QUOTE_STATUS_LABELS = {
  draft: "Brouillon",
  pending_validation: "En attente admin",
  approved: "Validé",
  rejected: "Rejeté",
  sent: "Envoyé",
  accepted: "Accepté",
  refused: "Refusé",
  purchase_order_created: "Accepté",
};

/* ACTA_QUOTE_BADGE_PAGINATION_V1 */
// ACTA_QUOTES_LEGACY_STATUS_COMPATIBILITY_V1
const QUOTE_DETAIL_PAGE_SIZE = 4;

const QUOTE_PDF_ALLOWED_STATUSES = new Set([
  "approved",
  "sent",
  "accepted",
  "refused",
  "purchase_order_created",
]);

const QUOTE_STATUS_BADGE_CLASSES = {
  draft: "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]",
  pending_validation: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  approved: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  rejected: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  sent: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  accepted: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  refused: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
  purchase_order_created: "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
};

// ACTA_PROSPECT_MEETING_MOBILE_LOT1_V1
// ACTA_MEETING_PARIS_FUTURE_GUARD_V1
const ACTA_PARIS_TIME_ZONE = "Europe/Paris";

function parisInputParts(date = new Date()) {
  const parts = new Intl.DateTimeFormat(
    "en-CA",
    {
      timeZone: ACTA_PARIS_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }
  ).formatToParts(date);

  const values = Object.fromEntries(
    parts.map(({ type, value }) => [
      type,
      value,
    ])
  );

  return {
    date: `${values.year}-${values.month}-${values.day}`,
    time: `${values.hour}:${values.minute}`,
  };
}

function localDateInputValue(date = new Date()) {
  return parisInputParts(date).date;
}

function localTimeInputValue(date = new Date()) {
  return parisInputParts(date).time;
}

function minimumMeetingStart(
  delayMinutes = 5
) {
  return new Date(
    Date.now() +
      delayMinutes * 60 * 1000
  );
}

function formatMeetingDateFr(value) {
  if (!value) {
    return "";
  }

  const [year, month, day] =
    String(value).split("-").map(Number);

  if (!year || !month || !day) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "fr-FR",
    {
      timeZone: ACTA_PARIS_TIME_ZONE,
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  ).format(
    new Date(
      Date.UTC(year, month - 1, day, 12)
    )
  );
}

function minimumMeetingInput(
  delayMinutes = 5
) {
  const raw = minimumMeetingStart(
    delayMinutes
  );

  raw.setSeconds(0, 0);

  const remainder =
    raw.getMinutes() % 5;

  if (remainder) {
    raw.setMinutes(
      raw.getMinutes() + (5 - remainder)
    );
  }

  const parts = parisInputParts(raw);

  return {
    ...parts,
    key: `${parts.date}T${parts.time}`,
  };
}

function quoteStatusBadgeClass(status) {
  return QUOTE_STATUS_BADGE_CLASSES[status] || "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]";
}



const NEXT_ACTION_LABELS = {
  meeting: "Rendez-vous",
  followup: "Relance",
  other: "Autre",
};

const FOLLOWUP_CHANNEL_LABELS = {
  email: "Email",
  phone: "Téléphone",
  whatsapp: "WhatsApp",
  other: "Autre / non précisé",
};

/**
 * Libellés des événements de tracking documents.
 */
const EVENT_LABEL = {
  share_opened: "Lien ouvert",
  document_viewed: "Document consulté",
  document_downloaded: "Document téléchargé",
  document_preview_viewed: "Aperçu consulté",
  document_preview_page_viewed: "Page consultée",
  share_message_received: "Message reçu",
  share_message_sent: "Message envoyé",
  share_meeting_proposed: "Rendez-vous proposé",
  share_meeting_proposed_by_commercial: "Rendez-vous proposé",
  share_meeting_selected: "Rendez-vous choisi",
  share_meeting_refused: "Rendez-vous refusé",
  share_meeting_accepted: "Rendez-vous accepté",
  share_meeting_confirmed: "Rendez-vous confirmé",
  share_meeting_cancelled: "Rendez-vous annulé",
  share_meeting_deleted: "Proposition de rendez-vous supprimée",
  share_reopened: "Partage rouvert",
  share_expired: "Partage expiré",
  quick_feedback: "Réponse rapide",
  html_interaction: "Interaction HTML",
  html_form_submit: "Formulaire soumis",
  html_calc: "Calcul effectué",
  html_click: "Clic interactif",
  html_complete: "Parcours complété",
};

/**
 * Formate une durée en secondes.
 */
function fmtSeconds(total) {
  if (!total) return "0s";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

/**
 * Formate une date ISO en date lisible française.
 */
function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
}

/**
 * Formate un montant commercial en euros.
 */
function fmtCurrency(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "—";
  }

  return new Intl.NumberFormat(
    "fr-FR",
    {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }
  ).format(amount);
}



/**
 * Formate une date commerciale sans décalage de fuseau.
 */
function fmtDate(value) {
  if (!value) return "—";

  const parts = String(value)
    .slice(0, 10)
    .split("-")
    .map(Number);

  if (
    parts.length !== 3 ||
    parts.some(
      (part) => !Number.isFinite(part)
    )
  ) {
    return "—";
  }

  const [year, month, day] = parts;

  return new Date(
    year,
    month - 1,
    day
  ).toLocaleDateString(
    "fr-FR",
    {
      dateStyle: "medium",
    }
  );
}


// acta_patch_lot3_5_machine_installation_v1
function formatGmaoDate(value) {
  if (!value) {
    return "";
  }

  try {
    return new Date(value).toLocaleDateString("fr-FR");
  } catch {
    return String(value);
  }
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffH = (Date.now() - d.getTime()) / 36e5;
  if (diffH < 1) return "à l'instant";
  if (diffH < 24) return `il y a ${Math.round(diffH)}h`;
  const days = Math.round(diffH / 24);
  if (days < 31) return `il y a ${days}j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

/**
 * Pagination locale simple.
 */
function paginate(items, page) {
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  return {
    page: safePage,
    totalPages,
    items: items.slice(start, start + PAGE_SIZE),
    start: items.length ? start + 1 : 0,
    end: Math.min(start + PAGE_SIZE, items.length),
  };
}

/**
 * Page détail prospect refondue.
 * Le contenu est piloté par une navigation latérale :
 * Vue d'ensemble, Activité, Notes, Documents, Messages, Rendez-vous.
 */
export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [
    searchParams,
    setSearchParams,
  ] = useSearchParams();
  const { user } = useAuth();
  // ACTA_PROSPECT_MOBILE_RC1_V1
  // ACTA_PROSPECT_ASSIGNMENT_ADMIN_ONLY_V1
  const canAssignCommercial =
    user?.role === "admin";

        const rawRequestedTab =
      searchParams.get("tab");

    const requestedTab =
      rawRequestedTab === "activity" ||
      rawRequestedTab === "notes"
        ? "comments"
        : rawRequestedTab === "commercial" ||
          rawRequestedTab === "pipeline"
          ? "information"
          : rawRequestedTab;

    // ACTA_PROSPECT_DIRECT_OPPORTUNITY_FORM_V1
    const requestedAction =

      searchParams.get("action") || "";

    // ACTA_PROSPECT_AGENT_WHATSAPP_BRIDGE_V1
    const requestedAgentCall =
      searchParams.get("agentCall") ||
      searchParams.get("agent_call") ||
      "";

    const consumeRequestedAgentCall =
      () => {
        const nextParams =
          new URLSearchParams(
            searchParams
          );

        nextParams.delete(
          "agentCall"
        );

        nextParams.delete(
          "agent_call"
        );

        setSearchParams(
          nextParams,
          {
            replace: true,
          }
        );
      };


    // ACTA_PROSPECT_DIRECT_ACTIVITY_FORM_V1

    const requestedActivityType =

      searchParams.get(

        "activityType"

      ) ||

      searchParams.get(

        "activity_type"

      ) ||

      "note";

    // ACTA_MEETING_REPORT_LINK_V1
    const requestedMeetingReportId =
      searchParams.get(
        "meetingReportId"
      ) || "";

    const requestedMeetingReportSource =
      searchParams.get(
        "meetingReportSource"
      ) || "";


    // ACTA_AGENT_PROSPECT_WORKFLOW_BRIDGE_V3
    const requestedAgentContent =
      searchParams.get(
        "agentContent"
      ) || "";

    const requestedAgentNextAction =
      searchParams.get(
        "agentNextAction"
      ) || "";

    const requestedAgentNextActionType =
      searchParams.get(
        "agentNextActionType"
      ) || "";

    const requestedAgentNextActionAt =
      searchParams.get(
        "agentNextActionAt"
      ) || "";

    const requestedAgentMeeting =
      searchParams.get(
        "agentMeeting"
      ) || "";

    const requestedAgentMeetingDate =
      searchParams.get(
        "agentMeetingDate"
      ) || "";

    const requestedAgentMeetingStart =
      searchParams.get(
        "agentMeetingStart"
      ) || "";

    const requestedAgentMeetingDuration =
      searchParams.get(
        "agentMeetingDurationMinutes"
      ) || "";

    const requestedAgentMeetingType =
      searchParams.get(
        "agentMeetingMeetingType"
      ) || "";

    const requestedAgentMeetingSubject =
      searchParams.get(
        "agentMeetingSubject"
      ) || "";


    // ACTA_PROSPECT_CALL_RETURN_WORKFLOW_V1
    const requestedCallOutcomeId =
      searchParams.get("callOutcome") ||
      searchParams.get("call_outcome") ||
      "";

    const shouldOpenOpportunityForm =
      requestedAction ===
      "new-opportunity";


// acta_fix_notification_message_exact_redirect_v2
// acta_fix_notification_message_safe_redirect_v3
const requestedShareId =
  searchParams.get("share_id") ||
  searchParams.get("shareId") ||
  "";

const requestedMessageId =
  searchParams.get("message_id") ||
  searchParams.get("messageId") ||
  "";

const canAccessWorkshops = ["admin", "manager", "user"].includes(user?.role);

const allowedTabs = [
    "sales",
    "information",
    "comments",
    "documents",
    "messages",
    "meetings",
    ...(canAccessWorkshops ? ["workshops"] : []),
  ];

  const initialTab =
    allowedTabs.includes(requestedTab)
      ? requestedTab
      : "sales";

  const [activeTab, setActiveTab] =
    useState(initialTab);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [
    activityStateSaving,
    setActivityStateSaving,
  ] = useState("");

  const [documents, setDocuments] = useState([]);
  const [machineCatalog, setMachineCatalog] = useState([]);
  const [docPicker, setDocPicker] = useState(false);
  const [selectedShareDocumentIds, setSelectedShareDocumentIds] = useState([]);
  const [commercialUsers, setCommercialUsers] = useState([]);
  const [commercialUsersLoading, setCommercialUsersLoading] = useState(false);
  const [assigningCommercial, setAssigningCommercial] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [
    collaboratorCandidates,
    setCollaboratorCandidates,
  ] = useState([]);
  const [
    collaboratorIdToAdd,
    setCollaboratorIdToAdd,
  ] = useState("");
  const [
    collaboratorsLoading,
    setCollaboratorsLoading,
  ] = useState(false);
  const [
    collaboratorsSaving,
    setCollaboratorsSaving,
  ] = useState(false);
  const [shareItem, setShareItem] = useState(null);

      // ACTA_UNIFIED_OPPORTUNITY_CREATE_MODAL_V1
    const [
      clientOpportunityModalOpen,
      setClientOpportunityModalOpen,
    ] = useState(false);

const [page, setPage] = useState(1);
  const [activityFilter, setActivityFilter] =
    useState("all");

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoToRead, setInfoToRead] = useState(null);

  const directActivityOpenedRef =
    useRef(false);

  const meetingReportOpenedRef =
    useRef(false);

  const agentMeetingOpenedRef =
    useRef(false);
  const [newInfo, setNewInfo] = useState("");

  // ACTA_MEETING_REPORT_LINK_V1
  const [
    meetingReportContext,
    setMeetingReportContext,
  ] = useState(null);

  // ACTA_MEETING_REPORT_VOICE_V1
  const meetingDictation =
    useSpeechDictation({
      language: "fr-FR",
      onTranscript: (
        transcript
      ) => {
        setNewInfo(
          (current) => {
            const cleanCurrent =
              String(
                current || ""
              ).trim();

            return cleanCurrent
              ? `${cleanCurrent} ${transcript}`
              : transcript;
          }
        );
      },
    });
  const [newInfoType, setNewInfoType] = useState("note");
  const [
    informationSaving,
    setInformationSaving,
  ] = useState(false);
  const [newInfoOutcome, setNewInfoOutcome] = useState("");
  const [nextActionType, setNextActionType] = useState("followup");
  const [nextActionChannel, setNextActionChannel] = useState("other");
  const [nextAction, setNextAction] = useState("");
  const [nextActionAt, setNextActionAt] = useState("");

  // ACTA_PROSPECT_CALL_OPTIONAL_FOLLOWUP_V1
  const [
    callOutcomeContext,
    setCallOutcomeContext,
  ] = useState("");

  const callOutcomeUi = {
    answered: {
      title: "A répondu",
      description:
        "Ajoutez un compte rendu ou accédez directement aux actions liées à la fiche.",
      contentLabel:
        "Compte rendu de l’appel (facultatif)",
      placeholder:
        "Ex. Le prospect souhaite une démonstration la semaine prochaine…",
    },
    no_answer: {
      title: "Pas de réponse",
      description:
        "Une relance est proposée automatiquement. Vous pouvez la modifier, la supprimer ou ne rien enregistrer.",
      contentLabel:
        "Compte rendu (facultatif)",
      placeholder:
        "Ex. Appel sans réponse…",
    },
    voicemail: {
      title: "Messagerie",
      description:
        "Le comportement est identique à « Pas de réponse » avec une relance proposée.",
      contentLabel:
        "Compte rendu (facultatif)",
      placeholder:
        "Ex. Message vocal laissé…",
    },
    callback: {
      title: "À rappeler",
      description:
        "Notez directement la date et l’heure de la prochaine relance.",
      contentLabel:
        "Note de rappel (facultatif)",
      placeholder:
        "Ex. À rappeler demain matin…",
    },
    not_interested: {
      title: "Pas intéressé",
      description:
        "Décrivez la raison si elle est utile et ajoutez éventuellement une prochaine action.",
      contentLabel:
        "Raison du refus (facultatif)",
      placeholder:
        "Ex. Budget insuffisant, projet reporté, déjà équipé…",
    },
  };

  const activeCallOutcomeUi =
    callOutcomeContext
      ? callOutcomeUi[
          callOutcomeContext
        ]
      : null;

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [
    contactDeleting,
    setContactDeleting,
  ] = useState(false);

  const [
    contactDeleteModalOpen,
    setContactDeleteModalOpen,
  ] = useState(false);

  const [
    contactDeleteImpact,
    setContactDeleteImpact,
  ] = useState([]);

  const [
    contactDeleteImpactLoading,
    setContactDeleteImpactLoading,
  ] = useState(false);

  const [
    purchaseIntentSaving,
    setPurchaseIntentSaving,
  ] = useState(false);
  const [pendingPurchaseIntent, setPendingPurchaseIntent] = useState(null);
  const [purchaseIntentNote, setPurchaseIntentNote] = useState("");
  const [documentPreview, setDocumentPreview] =
    useState(null);

  const [quotes, setQuotes] =
    useState([]);
  const [quotesLoading, setQuotesLoading] =
    useState(false);
  const [quoteSaving, setQuoteSaving] =
    useState(false);
  const [saleQuotePreview, setSaleQuotePreview] =
    useState(null);

  const [
    quoteRefusalModal,
    setQuoteRefusalModal,
  ] = useState(null);

  const [
    quoteRefusalReason,
    setQuoteRefusalReason,
  ] = useState("");

  const [quoteForm, setQuoteForm] =
    useState({
      title: "Devis commercial",
      machine_catalog_id: "",
      machine_name: "",
      quantity: "1",
      amount: "",
      notes: "",
    });

  const [selectedShareId, setSelectedShareId] =
    useState(requestedShareId);
  const [exchangeMessages, setExchangeMessages] = useState([]);
  const [exchangeMeetings, setExchangeMeetings] = useState([]);

  // Rendez-vous CRM internes, indépendants des partages.
  const [
    internalAppointments,
    setInternalAppointments,
  ] = useState([]);

  const [
    internalAppointmentsLoading,
    setInternalAppointmentsLoading,
  ] = useState(false);
  const [reply, setReply] = useState("");
  const [slots, setSlots] = useState([
    {
      id: "slot_1",
      date: "",
      start: "",
      durationMinutes: "30",
      locationType: "online",
      location: "",
    },
  ]);
  // ACTA_PROSPECT_MEETING_EDIT_DELETE_V1
  const [editingMeetingId, setEditingMeetingId] = useState(null);

  // ACTA_MEETING_COUNTER_PROPOSAL_STATE_V1
  const [
    counterMeetingId,
    setCounterMeetingId,
  ] = useState(null);

  const [standaloneMeetingOpen, setStandaloneMeetingOpen] =
    useState(false);

  const [standaloneMeetingSaving, setStandaloneMeetingSaving] =
    useState(false);

  const [standaloneMeetingLinks, setStandaloneMeetingLinks] =
    useState(null);

  // ACTA_MEETING_CLIENT_SHARE_V1
  const [
    standaloneMeetingShareLinks,
    setStandaloneMeetingShareLinks,
  ] = useState(null);

  // ACTA_STANDALONE_MEETING_CONFIRMATION_V2
  const [standaloneMeetingSaved, setStandaloneMeetingSaved] =
    useState(null);

  // ACTA_STANDALONE_MEETING_REAL_EDIT_V1
  const [standaloneMeetingAppointmentId, setStandaloneMeetingAppointmentId] =
    useState(null);

  const [standaloneMeetingForm, setStandaloneMeetingForm] =
    useState({
      meetingType: "online",
      date: "",
      start: "",
      durationMinutes: "30",
      subject: "",
      notes: "",
      location: "",
    });

  const load = () =>
    prospectsApi.activity(id)
      .then(setData)
      .catch((e) =>
        setErr(
          formatApiError(
            e.response?.data?.detail
          ) || e.message
        )
      );

  const loadInternalAppointments = async () => {
    setInternalAppointmentsLoading(
      true
    );

    try {
      const items =
        await appointmentsApi.listForProspect(
          id
        );

      setInternalAppointments(
        Array.isArray(items)
          ? items
          : []
      );

    } catch {
      setInternalAppointments([]);

    } finally {
      setInternalAppointmentsLoading(
        false
      );
    }
  };


  // ACTA_ACTIVITY_USER_STATE_V1
  const updateActivityState = async (
    item,
    patch
  ) => {
    if (!item?.kind || !item?.id) {
      toast.error(
        "Cette activité ne peut pas être modifiée"
      );

      return false;
    }

    const savingKey =
      `${item.kind}:${item.id}`;

    setActivityStateSaving(
      savingKey
    );

    try {
      await prospectsApi.updateActivityState(
        id,
        {
          activityKind: item.kind,
          activityId: String(item.id),
          ...patch,
        }
      );

      await load();

      return true;
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Impossible de modifier cette activité"
      );

      return false;
    } finally {
      setActivityStateSaving("");
    }
  };

  useEffect(() => {
    if (!canAssignCommercial) {
      setCommercialUsers([]);
      return;
    }

    let cancelled = false;

    async function loadCommercialUsers() {
      setCommercialUsersLoading(true);
      try {
        const result = await prospectsApi.listCommercialUsers();
        if (!cancelled) {
          setCommercialUsers(Array.isArray(result) ? result : []);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error("Impossible de charger les commerciaux");
        }
      } finally {
        if (!cancelled) {
          setCommercialUsersLoading(false);
        }
      }
    }

    loadCommercialUsers();

    return () => {
      cancelled = true;
    };
  }, [canAssignCommercial]);

  const assignCommercial = async (assignedUserId) => {
    if (!canAssignCommercial || !p?.id) {
      return;
    }

    setAssigningCommercial(true);

    try {
      const updated =
        await prospectsApi.assignCommercial(
          p.id,
          {
            assigned_user_id:
              assignedUserId,
          }
        );

      setP(updated);
      await load();

      toast.success(
        "Commercial responsable mis à jour"
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        )
      );
    } finally {
      setAssigningCommercial(false);
    }
  };


  const loadCollaborators = async () => {
    if (!id) {
      return;
    }

    setCollaboratorsLoading(true);

    try {
      const [current, candidates] =
        await Promise.all([
          prospectsApi.listCollaborators(id),
          prospectsApi.listCollaboratorCandidates(
            id
          ),
        ]);

      setCollaborators(
        Array.isArray(current?.items)
          ? current.items
          : []
      );

      setCollaboratorCandidates(
        Array.isArray(candidates)
          ? candidates
          : []
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Impossible de charger les collaborateurs"
      );
    } finally {
      setCollaboratorsLoading(false);
    }
  };


  const saveCollaborators = async (
    userIds
  ) => {
    setCollaboratorsSaving(true);

    try {
      await prospectsApi.updateCollaborators(
        id,
        userIds
      );

      setCollaboratorIdToAdd("");
      await loadCollaborators();
      await load();

      toast.success(
        "Collaborateurs mis à jour"
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Impossible de modifier les collaborateurs"
      );
    } finally {
      setCollaboratorsSaving(false);
    }
  };


  const addCollaborator = async () => {
    if (!collaboratorIdToAdd) {
      return;
    }

    await saveCollaborators([
      ...collaborators
        .map((item) => item.id)
        .filter(Boolean),
      collaboratorIdToAdd,
    ]);
  };


  const removeCollaborator = async (
    collaboratorId
  ) => {
    await saveCollaborators(
      collaborators
        .map((item) => item.id)
        .filter(
          (userId) =>
            userId &&
            userId !== collaboratorId
        )
    );
  };


  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    loadCollaborators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadInternalAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    documentsApi.list().then((d) => setDocuments(d || [])).catch(() => {});
  }, []);

  useEffect(() => {
    machineCatalogApi
      .list()
      .then((items) =>
        setMachineCatalog(items || [])
      )
      .catch(() => {
        setMachineCatalog([]);
      });
  }, []);

  const p = data?.prospect;
  const shares = data?.shares || [];
  const sharedDocuments = data?.sharedDocuments || [];
  const contactDocuments = data?.contactDocuments || [];

  // ACTA_SHARED_DOCUMENT_NAMES_V1
  const resolvedSharedDocuments = useMemo(() => {
    const documentNamesById = new Map();
    const sourceDocumentsById = new Map();
    const shareNamesByDocumentId = new Map();

    const registerDocument = (document) => {
      const documentId =
        document?.id ||
        document?.documentId ||
        document?.document_id;

      const documentName =
        document?.documentName ||
        document?.name ||
        document?.filename ||
        document?.originalName ||
        document?.original_name ||
        document?.title;

      if (documentId && documentName) {
        documentNamesById.set(
          String(documentId),
          String(documentName)
        );
      }
    };

    (documents || []).forEach((document) => {
      registerDocument(document);

      const documentId =
        document?.id ||
        document?.documentId ||
        document?.document_id;

      if (documentId) {
        sourceDocumentsById.set(String(documentId), document);
      }
    });
    (sharedDocuments || []).forEach(registerDocument);

    (shares || []).forEach((share) => {
      const shareItems = Array.isArray(share?.items)
        ? share.items
        : [];

      shareItems.forEach((item) => {
        const documentId =
          item?.id ||
          item?.documentId ||
          item?.document_id;

        const documentName =
          item?.documentName ||
          item?.name ||
          item?.filename ||
          item?.originalName ||
          item?.original_name ||
          item?.title;

        if (documentId && documentName) {
          shareNamesByDocumentId.set(
            String(documentId),
            String(documentName)
          );
        }
      });
    });

    return (sharedDocuments || []).map(
      (document) => {
        const documentId =
          document?.documentId ||
          document?.document_id ||
          document?.id;

        const rawResolvedName =
          document?.documentName ||
          document?.name ||
          document?.filename ||
          document?.originalName ||
          document?.original_name ||
          document?.title ||
          (
            documentId
              ? documentNamesById.get(
                  String(documentId)
                )
              : ""
          ) ||
          (
            documentId
              ? shareNamesByDocumentId.get(
                  String(documentId)
                )
              : ""
          );

        const resolvedName =
          cleanHistoryDocumentName(
            rawResolvedName
          );

        return {
          ...document,
          documentName: resolvedName,
          sourceDocument:
            documentId
              ? sourceDocumentsById.get(String(documentId)) || null
              : null,
        };
      }
    );
  }, [
    documents,
    shares,
    sharedDocuments,
  ]);

  const resolvedContactDocuments = useMemo(() => {
    const latestShareByDocumentId = new Map();

    (resolvedSharedDocuments || []).forEach((document) => {
      const documentId =
        document?.documentId ||
        document?.document_id ||
        document?.id;

      if (
        documentId &&
        !latestShareByDocumentId.has(String(documentId))
      ) {
        latestShareByDocumentId.set(
          String(documentId),
          document
        );
      }
    });

    return (contactDocuments || []).map((document) => {
      const documentId =
        document?.id ||
        document?.documentId ||
        document?.document_id;
      const share =
        documentId
          ? latestShareByDocumentId.get(String(documentId))
          : null;

      return {
        ...(share || {}),
        ...document,
        documentId,
        documentName:
          document?.name ||
          document?.documentName ||
          share?.documentName ||
          "Document",
        sourceDocument: document,
      };
    });
  }, [contactDocuments, resolvedSharedDocuments]);


  // ACTA_EXCHANGE_SHARE_DOCUMENT_LABELS_V1
  const exchangeShares = useMemo(() => {
    const documentNamesById = new Map();

    const registerDocument = (document) => {
      const documentId =
        document?.id ||
        document?.documentId ||
        document?.document_id;

      const documentName =
        document?.name ||
        document?.filename ||
        document?.originalName ||
        document?.title;

      if (documentId && documentName) {
        documentNamesById.set(
          String(documentId),
          String(documentName)
        );
      }
    };

    (documents || []).forEach(registerDocument);
    (sharedDocuments || []).forEach(registerDocument);

    return shares.map((share) => {
      const itemIds = (share.items || [])
        .filter(
          (item) =>
            item &&
            (
              !item.type ||
              item.type === "document"
            )
        )
        .map((item) => item.id)
        .filter(Boolean);

      const documentIds = Array.from(
        new Set(
          [
            ...(share.snapshotDocumentIds || []),
            ...(share.documentId
              ? [share.documentId]
              : []),
            ...itemIds,
          ]
            .filter(Boolean)
            .map(String)
        )
      );

      const namesFromDocuments = documentIds
        .map((documentId) =>
          documentNamesById.get(documentId)
        )
        .filter(Boolean);

      const namesFromItems = (share.items || [])
        .map(
          (item) =>
            item?.name ||
            item?.filename ||
            item?.originalName ||
            item?.title
        )
        .filter(Boolean);

      return {
        ...share,
        exchangeDocumentNames: Array.from(
          new Set([
            ...namesFromDocuments,
            ...namesFromItems,
          ])
        ),
      };
    });
  }, [
    shares,
    documents,
    sharedDocuments,
  ]);



  useEffect(() => {
    if (
      requestedShareId &&
      shares.some(
        (share) =>
          share.id === requestedShareId
      )
    ) {
      setSelectedShareId(
        requestedShareId
      );

      return;
    }

    if (
      !selectedShareId &&
      shares.length
    ) {
      setSelectedShareId(
        shares[0].id
      );
    }
  }, [
    requestedShareId,
    selectedShareId,
    shares,
  ]);

  const complementaryInfos = useMemo(() => {
    const allowedTypes = [
      "note",
      "complementary_info",
    ];

    return (data?.notes || [])
      .filter((note) =>
        allowedTypes.includes(
          note.activityType ||
          note.activity_type ||
          "note"
        )
      )
      .sort((a, b) =>
        (
          a.createdAt ||
          a.activityDate
        ) <
        (
          b.createdAt ||
          b.activityDate
        )
          ? 1
          : -1
      );
  }, [data]);

  function cleanHistoryDocumentName(
    value
  ) {
    const clean = String(
      value || ""
    ).trim();

    if (
      !clean ||
      ["-", "--", "—", "–"].includes(clean)
    ) {
      return "Document partagé";
    }

    return clean;
  }

  const activityStateByKey =
    useMemo(() => {
      return new Map(
        (
          data?.activityStates ||
          []
        ).map(
          (state) => [
            `${state.activityKind}:${state.activityId}`,
            state,
          ]
        )
      );
    }, [
      data?.activityStates,
    ]);

  const timeline = useMemo(() => {
    if (!data) return [];

    const contactName =
      [
        data?.prospect?.firstName,
        data?.prospect?.lastName,
      ]
        .filter(Boolean)
        .join(" ") ||
      data?.prospect?.company ||
      "Le prospect";

    const events = (
      data.events || []
    ).map((event) => {
      const eventType =
        event.eventType || "";

      const documentName =
        cleanHistoryDocumentName(
          event.documentName
        );

      const shareId =
        event.shareId ||
        event.share_id ||
        "";

      const relatedShare =
        (data.shares || []).find(
          (share) =>
            share.id === shareId
        );

      const shareToken =
        event.shareToken ||
        event.share_token ||
        relatedShare?.token ||
        "";

      const category =
        eventType ===
          "prospect_commercial_reassigned"
          ? "commercial"
          : eventType.includes("message")
            ? "messages"
            : eventType.includes("meeting")
            ? "meetings"
            : (
              eventType.includes("document") ||
              eventType.includes("share_opened") ||
              eventType.includes("share_reopened") ||
              eventType.includes("share_expired")
            )
              ? "documents"
              : "system";

      const EventIcon =
        category === "messages"
          ? ChatCircleText
          : category === "meetings"
            ? Calendar
            : category === "documents"
              ? FileText
              : Sparkle;

      const previousCommercialName =
        event.previousAssignedUserName ||
        event.previousAssignedUserId ||
        "Non assigné";

      const newCommercialName =
        event.assignedUserName ||
        event.assignedUserId ||
        "Non assigné";

      const explicitTitles = {
        prospect_commercial_reassigned:
          `Commercial responsable modifié : ${previousCommercialName} → ${newCommercialName}`,
        share_opened:
          `${contactName} a ouvert le lien de partage`,
        document_viewed:
          `${contactName} a consulté « ${documentName} »`,
        document_downloaded:
          `${contactName} a téléchargé « ${documentName} »`,
        document_preview_viewed:
          `${contactName} a consulté l’aperçu de « ${documentName} »`,
        document_preview_page_viewed:
          `${contactName} a consulté une page de « ${documentName} »`,
        share_message_received:
          `Nouveau message reçu de ${contactName}`,
        share_message_sent:
          `Un message a été envoyé à ${contactName}`,
        share_meeting_proposed:
          `Un rendez-vous a été proposé à ${contactName}`,
        share_meeting_proposed_by_commercial:
          `Un rendez-vous a été proposé à ${contactName}`,
        share_meeting_selected:
          `${contactName} a choisi un créneau de rendez-vous`,
        share_meeting_refused:
          `${contactName} a refusé le rendez-vous proposé`,
        share_meeting_accepted:
          `${contactName} a accepté le rendez-vous`,
        share_meeting_confirmed:
          `Le rendez-vous avec ${contactName} est confirmé`,
        share_meeting_cancelled:
          `Le rendez-vous avec ${contactName} a été annulé`,
        share_meeting_deleted:
          `Une proposition de rendez-vous a été supprimée`,
        share_reopened:
          `Le partage avec ${contactName} a été rouvert`,
        share_expired:
          `Le lien de partage envoyé à ${contactName} a expiré`,
        quick_feedback:
          `${contactName} a envoyé une réponse rapide`,
        html_form_submit:
          `${contactName} a envoyé un formulaire`,
        html_complete:
          `${contactName} a terminé le contenu interactif`,
      };

      const activityState =
        activityStateByKey.get(
          `event:${event.id}`
        ) || {};

      const requiresAction =
        [
          "share_message_received",
          "share_meeting_refused",
          "share_expired",
        ].includes(eventType) &&
        !activityState.read;

      const isCompleted = [
        "share_meeting_accepted",
        "share_meeting_confirmed",
      ].includes(eventType);

      const actionType =
        eventType ===
          "share_message_received"
          ? "reply"
          : eventType ===
              "share_meeting_refused"
            ? "meeting"
            : (
              category === "documents" &&
              shareId
            )
              ? "share"
              : "";

      const actionLabel =
        actionType === "reply"
          ? "Répondre"
          : actionType === "meeting"
            ? "Proposer un créneau"
            : actionType === "share"
              ? "Voir le partage"
              : "";

      let detail =
        event.quickFeedbackLabel ||
        event.message ||
        event.content ||
        (
          eventType ===
            "prospect_commercial_reassigned"
            ? `Modification effectuée par ${
                event.changedByName ||
                "un utilisateur autorisé"
              }`
            : ""
        );

      if (
        !detail &&
        eventType !== "document_viewed" &&
        event.documentName
      ) {
        detail =
          `Document concerné : ${event.documentName}`;
      }

      return {
        kind: "event",
        category,
        eventType,
        id: event.id,
        when: event.createdAt,
        title:
          explicitTitles[eventType] ||
          EVENT_LABEL[eventType] ||
          "Une activité a été enregistrée",
        subtitle: detail,
        author:
          category === "system"
            ? "Information système"
            : "",
        icon: EventIcon,
        shareId,
        shareToken,
        documentId:
          event.documentId ||
          event.document_id ||
          "",
        messageId:
          event.messageId ||
          event.message_id ||
          "",
        meetingId:
          event.meetingId ||
          event.meeting_id ||
          "",
        actionType,
        actionLabel,
        requiresAction,
        isCompleted,
        isRead: Boolean(
          activityState.read
        ),
        hidden: Boolean(
          activityState.hidden
        ),
        technical:
          category === "system" ||
          eventType.startsWith("html_") ||
          eventType ===
            "document_preview_page_viewed",
      };
    });

    const notes = (
      data.notes || []
    ).map((note) => {
      const type =
        note.activityType ||
        note.activity_type ||
        "note";

      const activityMeta =
        ACTIVITY_TYPES[type] ||
        ACTIVITY_TYPES.note;

      const commercialTitles = {
        call: `Appel effectué avec ${contactName}`,
        email: `E-mail envoyé à ${contactName}`,
        email_sent: `E-mail envoyé à ${contactName}`,
        email_received: `E-mail reçu de ${contactName}`,
        meeting: `Rendez-vous avec ${contactName}`,
        followup: `Relance effectuée auprès de ${contactName}`,
        note: "Note commerciale ajoutée",
      };

      const noteState =
        activityStateByKey.get(
          `note:${note.id}`
        ) || {};

      const noteActivityDate =
        note.activityDate ||
        note.activity_date ||
        note.createdAt;

      const noteActionDate =
        note.nextActionAt ||
        note.next_action_at ||
        noteActivityDate;

      const isUpcomingMeeting =
        type === "meeting" &&
        new Date(
          noteActionDate || 0
        ).getTime() > Date.now();

      return {
        kind: "note",
        category:
          note.system
            ? "system"
            : "commercial",
        id: note.id,
        when:
          note.activityDate ||
          note.activity_date ||
          note.createdAt,
        title: note.system
          ? (
            note.content ||
            "La fiche commerciale a été mise à jour"
          )
          : (
            commercialTitles[type] ||
            activityMeta.label
          ),
        subtitle: note.system
          ? ""
          : note.content,
        author:
          note.authorName || "—",
        icon: activityMeta.icon,
        system: note.system,
        outcome: note.outcome,
        nextAction:
          note.nextAction ||
          note.next_action,
        nextActionType:
          note.nextActionType ||
          note.next_action_type,
        nextActionAt:
          note.nextActionAt ||
          note.next_action_at,
        followupStatus:
          note.followupStatus ||
          note.followup_status,
        callChannel:
          note.callChannel ||
          note.call_channel ||
          "",
        callResultCode:
          note.callResultCode ||
          note.call_result_code ||
          "",
        mailMessageId:
          note.mailMessageId ||
          note.mail_message_id ||
          "",
        actionType:
          ["email_sent", "email_received"].includes(type) &&
          (
            note.mailMessageId ||
            note.mail_message_id
          )
            ? "mail"
            : type === "call" &&
          Boolean(note.callChannel || note.call_channel) &&
          !(
            note.callResultCode ||
            note.call_result_code
          )
            ? "categorize_call"
            : "",
        actionLabel:
          ["email_sent", "email_received"].includes(type) &&
          (
            note.mailMessageId ||
            note.mail_message_id
          )
            ? "Ouvrir l’e-mail"
            : type === "call" &&
          Boolean(note.callChannel || note.call_channel) &&
          !(
            note.callResultCode ||
            note.call_result_code
          )
            ? "Catégoriser l’appel"
            : "",
        requiresAction:
          !noteState.read &&
          !isUpcomingMeeting &&
          (
            (
              type === "call" &&
              Boolean(note.callChannel || note.call_channel) &&
              !(
                note.callResultCode ||
                note.call_result_code
              )
            ) ||
            (
              Boolean(
                note.nextAction ||
                note.next_action
              ) &&
              (
                note.followupStatus ||
                note.followup_status
              ) !== "completed" &&
              (
                note.actionStatus ||
                note.action_status
              ) !== "completed"
            )
          ),
        isRead: Boolean(
          noteState.read
        ),
        hidden: Boolean(
          noteState.hidden
        ),
        isCompleted:
          (
            (
              note.followupStatus ||
              note.followup_status
            ) === "completed" ||
            (
              note.actionStatus ||
              note.action_status
            ) === "completed"
          ),
        technical: Boolean(note.system),
        canDelete:
          !note.system &&
          (
            user?.role === "admin" ||
            note.authorId === user?.id
          ),
      };
    });

    return [
      ...events,
      ...notes,
    ].sort((a, b) =>
      a.when < b.when ? 1 : -1
    );
  }, [
    data,
    user,
    activityStateByKey,
  ]);

  const unifiedTimeline = useMemo(() => {
    // ACTA_INTERNAL_APPOINTMENTS_ACTIVITY_V1
    // Projection visuelle uniquement :
    // prospect_appointments reste la source de vérité.
    const appointmentItems =
      (internalAppointments || []).map(
        (appointment) => {
          const status =
            appointment.status ||
            "scheduled";

          const statusLabel =
            status === "completed"
              ? "Terminé"
              : status === "cancelled"
                ? "Annulé"
                : "Planifié";

          const meetingTypeLabel =
            appointment.meetingType ===
              "physical"
              ? "Physique"
              : "En ligne";

          const details = [
            `Prévu le ${
              fmtDateTime(
                appointment.startAt
              )
            }`,
            `${
              appointment.durationMinutes ||
              30
            } min`,
            meetingTypeLabel,
            appointment.location || "",
            statusLabel,
          ]
            .filter(Boolean)
            .join(" · ");

          return {
            kind: "appointment",
            category: "meetings",
            id: appointment.id,
            when:
              appointment.createdAt ||
              appointment.startAt,
            title:
              status === "cancelled"
                ? `Rendez-vous annulé : ${
                    appointment.subject ||
                    "Rendez-vous"
                  }`
                : status === "completed"
                  ? `Rendez-vous terminé : ${
                      appointment.subject ||
                      "Rendez-vous"
                    }`
                  : `Rendez-vous planifié : ${
                      appointment.subject ||
                      "Rendez-vous"
                    }`,
            subtitle: details,
            icon: Calendar,
            appointmentId:
              appointment.id,
            appointment,
            requiresAction: false,
            isRead: true,
            hidden: false,
            isCompleted:
              status === "completed",
            technical: false,
            canDelete: false,
          };
        }
      );

    const documentItems =
      (resolvedSharedDocuments || []).map(
        (document, index) => {
          const safeDocumentName =
            cleanHistoryDocumentName(
              document.documentName
            );

          const status =
            document.revoked
              ? "Révoqué"
              : document.expired
                ? "Expiré"
                : "Actif";

          const activityId =
            `${document.shareId || "share"}-${document.documentId || index}`;

          const documentState =
            activityStateByKey.get(
              `document:${activityId}`
            ) || {};

          return {
            kind: "document",
            category: "documents",
            id: activityId,
            when: document.createdAt,
            title:
              safeDocumentName ===
                "Document partagé"
                ? "Document partagé"
                : `Document « ${safeDocumentName} » partagé`,
            subtitle: [
              `Partagé par ${
                document.createdByName ||
                "Utilisateur"
              }`,
              `${
                Number(
                  document.openCount || 0
                )
              } ouverture(s)`,
              document.lastOpenedAt
                ? `dernière consultation ${
                    relativeTime(
                      document.lastOpenedAt
                    )
                  }`
                : "",
            ]
              .filter(Boolean)
              .join(" · "),
            author:
              document.createdByName ||
              "Utilisateur",
            icon: FileText,
            document,
            status,
            shareId:
              document.shareId || "",
            shareToken:
              document.shareToken || "",
            documentId:
              document.documentId || "",
            actionType:
              document.shareId
                ? "share"
                : "",
            actionLabel:
              document.shareId
                ? "Voir le partage"
                : "",
            requiresAction:
              !documentState.read,
            isRead: Boolean(
              documentState.read
            ),
            hidden: Boolean(
              documentState.hidden
            ),
            isCompleted: false,
            technical: false,
          };
        }
      );

    return [
      ...timeline,
      ...appointmentItems,
      ...documentItems,
    ].sort((a, b) => {
      const aTime =
        new Date(a.when || 0).getTime();
      const bTime =
        new Date(b.when || 0).getTime();

      return bTime - aTime;
    });
  }, [
    timeline,
    internalAppointments,
    resolvedSharedDocuments,
    activityStateByKey,
  ]);

  const filteredTimeline = useMemo(() => {
    const visibleTimeline =
      unifiedTimeline.filter(
        (item) =>
          !item.technical &&
          !item.hidden
      );

    if (activityFilter === "all") {
      return visibleTimeline;
    }

    if (activityFilter === "todo") {
      return visibleTimeline.filter(
        (item) =>
          item.requiresAction
      );
    }

    return visibleTimeline.filter(
      (item) =>
        item.category === activityFilter
    );
  }, [
    activityFilter,
    unifiedTimeline,
  ]);

  const loadExchange = async (shareId = selectedShareId) => {
    if (!shareId) return;
    try {
      const [messages, meetings] = await Promise.all([
        sharesApi.listExchangeMessages(shareId),
        sharesApi.listExchangeMeetings(shareId),
      ]);
      setExchangeMessages(messages || []);
      setExchangeMeetings(meetings || []);
    } catch {
      toast.error("Échanges impossibles à charger");
    }
  };

  useEffect(() => {
    if (
      selectedShareId &&
      (
        activeTab === "messages" ||
        activeTab === "meetings"
      )
    ) {
      loadExchange(
        selectedShareId
      );
    }
  }, [
    selectedShareId,
    activeTab,
  ]);

  useEffect(() => {
    if (
      allowedTabs.includes(
        requestedTab
      )
    ) {
      setActiveTab(
        requestedTab
      );
    }
  }, [
    requestedTab,
  ]);

  const loadQuotes = async () => {
    if (!p?.id) {
      return;
    }

    setQuotesLoading(true);

    try {
      const result = await quotesApi.list({
        prospectId: id,
        clientId:
          p.lifecycleStage === "client"
            ? id
            : "",
        limit: 80,
      });

      setQuotes(result.items || []);
    } catch {
      setQuotes([]);
    } finally {
      setQuotesLoading(false);
    }
  };


  useEffect(() => {
    if (!p?.id) {
      return;
    }

    loadQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    p?.id,
    p?.lifecycleStage,
  ]);

  useEffect(() => {
    if (
      activeTab !== "messages" ||
      !requestedMessageId ||
      !exchangeMessages.length
    ) {
      return;
    }

    const scrollToMessage = () => {
      const target =
        document.querySelector(
          `[data-message-id="${requestedMessageId}"]`
        );

      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        target.classList.add(
          "ring-2",
          "ring-[var(--do-accent)]",
          "ring-offset-2"
        );

        window.setTimeout(() => {
          target.classList.remove(
            "ring-2",
            "ring-[var(--do-accent)]",
            "ring-offset-2"
          );
        }, 2500);

        return true;
      }

      return false;
    };

    if (scrollToMessage()) {
      return;
    }

    const timer = window.setTimeout(
      scrollToMessage,
      250
    );

    return () =>
      window.clearTimeout(timer);
  }, [
    activeTab,
    exchangeMessages,
    requestedMessageId,
  ]);

  // ACTA_MEETING_REPORT_INFORMATION_WORKFLOW_V1
  // Le compte rendu de rendez-vous utilise la zone
  // Informations complémentaires existante.
  //
  // Il ne passe pas par le formulaire générique
  // "Ajouter une activité" et ne propose donc aucune
  // prochaine action.
  useEffect(() => {
    if (
      requestedAction !==
        "meeting-report"
    ) {
      meetingReportOpenedRef.current =
        false;

      return;
    }

    if (
      !data?.prospect ||
      meetingReportOpenedRef.current
    ) {
      return;
    }

    if (
      !requestedMeetingReportId ||
      ![
        "prospect",
        "prospect_appointment",
        "share_meeting",
      ].includes(
        requestedMeetingReportSource
      )
    ) {
      return;
    }

    meetingReportOpenedRef.current =
      true;

    setMeetingReportContext({
      id: requestedMeetingReportId,
      source:
        requestedMeetingReportSource,
    });

    setActiveTab("information");
    setNewInfoType("meeting");
    setNewInfo("");
    setPage(1);

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    [
      "action",
      "meetingReportId",
      "meetingReportSource",
    ].forEach(
      (key) =>
        nextParams.delete(key)
    );

    setSearchParams(
      nextParams,
      {
        replace: true,
      }
    );
  }, [
    requestedAction,
    requestedMeetingReportId,
    requestedMeetingReportSource,
    data?.prospect?.id,
  ]);


  // ACTA_PROSPECT_DIRECT_ACTIVITY_EFFECT_V1
  // ACTA_AGENT_PROSPECT_ACTIVITY_PREFILL_V3
  useEffect(() => {
    if (
      requestedAction !==
        "new-activity"
    ) {
      directActivityOpenedRef.current =
        false;

      return;
    }

    // ACTA_MEETING_REPORT_WAIT_FOR_CONTACT_V1
    // L'ouverture directe d'une activité doit attendre
    // que la fiche soit réellement chargée.
    if (
      !data?.prospect ||
      directActivityOpenedRef.current
    ) {
      return;
    }

    const allowedActivityTypes =
      new Set([
        "call",
        "email",
        "meeting",
        "followup",
        "note",
      ]);

    const activityType =
      allowedActivityTypes.has(
        requestedActivityType
      )
        ? requestedActivityType
        : "note";

    directActivityOpenedRef.current =
      true;

    setActiveTab("comments");
    setPage(1);

    openCommercialActivity(
      activityType
    );

    if (
      activityType === "meeting" &&
      requestedMeetingReportId &&
      [
        "prospect",
        "prospect_appointment",
        "share_meeting",
      ].includes(
        requestedMeetingReportSource
      )
    ) {
      setMeetingReportContext({
        id: requestedMeetingReportId,
        source:
          requestedMeetingReportSource,
      });
    }

    if (
      requestedAgentContent
    ) {
      setNewInfo(
        requestedAgentContent
      );
    }

    if (
      requestedAgentNextAction
    ) {
      setNextAction(
        requestedAgentNextAction
      );
    }

    if (
      requestedAgentNextActionType
    ) {
      setNextActionType(
        requestedAgentNextActionType
      );
    }

    if (
      requestedAgentNextActionAt
    ) {
      setNextActionAt(
        requestedAgentNextActionAt
      );
    }

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    [
      "action",
      "activityType",
      "activity_type",
      "meetingReportId",
      "meetingReportSource",
      "agentContent",
      "agentNextAction",
      "agentNextActionType",
      "agentNextActionAt",
    ].forEach(
      (key) =>
        nextParams.delete(
          key
        )
    );

    setSearchParams(
      nextParams,
      {
        replace: true,
      }
    );
  }, [
    requestedAction,
    requestedActivityType,
    requestedMeetingReportId,
    requestedMeetingReportSource,
    requestedAgentContent,
    requestedAgentNextAction,
    requestedAgentNextActionType,
    requestedAgentNextActionAt,
    data?.prospect?.id,
  ]);

  // ACTA_AGENT_PROSPECT_MEETING_PREFILL_V4
  // Hook déclaré avant tous les early returns React.
  // Il préremplit le modal Acta existant sans créer
  // de nouvelle logique d'écriture métier.
  useEffect(() => {
    if (
      requestedAgentMeeting !==
        "create"
    ) {
      agentMeetingOpenedRef.current =
        false;

      return;
    }

    if (
      agentMeetingOpenedRef.current ||
      !data
    ) {
      return;
    }

    const prospect =
      data?.prospect ||
      data ||
      {};

    const contactName =
      prospect.company ||
      [
        prospect.firstName,
        prospect.lastName,
      ]
        .filter(Boolean)
        .join(" ") ||
      "Contact";

    const suggestedStart =
      minimumMeetingInput(15);

    const meetingType =
      [
        "online",
        "physical",
      ].includes(
        requestedAgentMeetingType
      )
        ? requestedAgentMeetingType
        : "online";

    agentMeetingOpenedRef.current =
      true;

    setStandaloneMeetingLinks(null);
    setStandaloneMeetingShareLinks(null);
    setStandaloneMeetingSaved(null);
    setStandaloneMeetingAppointmentId(null);

    setStandaloneMeetingForm({
      meetingType,
      date:
        requestedAgentMeetingDate ||
        suggestedStart.date,
      start:
        requestedAgentMeetingStart ||
        suggestedStart.time,
      durationMinutes:
        requestedAgentMeetingDuration ||
        "30",
      subject:
        requestedAgentMeetingSubject ||
        `Rendez-vous ${contactName}`,
      notes: "",
      location:
        meetingType === "physical"
          ? buildProspectMeetingAddress(
              prospect
            )
          : "",
    });

    setStandaloneMeetingOpen(true);

    const nextParams =
      new URLSearchParams(
        searchParams
      );

    [
      "agentMeeting",
      "agentMeetingDate",
      "agentMeetingStart",
      "agentMeetingDurationMinutes",
      "agentMeetingMeetingType",
      "agentMeetingSubject",
    ].forEach(
      (key) =>
        nextParams.delete(
          key
        )
    );

    setSearchParams(
      nextParams,
      {
        replace: true,
      }
    );
  }, [
    data,
    requestedAgentMeeting,
    requestedAgentMeetingDate,
    requestedAgentMeetingStart,
    requestedAgentMeetingDuration,
    requestedAgentMeetingType,
    requestedAgentMeetingSubject,
  ]);


  if (err) return <div className="p-8 text-sm text-[var(--do-accent)]" data-testid="prospect-error">{err}</div>;
  if (!data || !p) return <div className="p-8 label-eyebrow lirah-pulse">Chargement…</div>;

  // ACTA_CONTACT_EDIT_WITHOUT_OPPORTUNITY_V1
  const openEdit = () => {
    setEditForm({
      firstName: p.firstName || "",
      lastName: p.lastName || "",
      email: p.email || "",
      phone: p.phone || "",
      company: p.company || "",
      position: p.position || "",
      address:
        typeof p.address === "string"
          ? p.address
          : "",
      postalCode: p.postalCode || "",
      city: p.city || "",
      country: p.country || "FR",
      lat: p.lat ?? null,
      lng: p.lng ?? null,
      lifecycleStage:
        p.lifecycleStage === "client"
          ? "client"
          : "prospect",
    });

    setEditOpen(true);
  };

  const resolveEditAddress = async () => {
    const address = String(
      editForm.address || "",
    ).trim();
    const postalCode = editForm.postalCode;
    const city = editForm.city;

    if (!address) return;

    try {
      const resolved =
        await prospectsApi.resolveLocation(address);

      setEditForm((current) => {
        if (
          String(current.address || "").trim() !== address ||
          current.postalCode !== postalCode ||
          current.city !== city
        ) {
          return current;
        }

        const resolvedPostalCode = String(
          resolved?.postalCode || "",
        ).trim();
        const resolvedCity = String(
          resolved?.city || "",
        ).trim();

        if (!resolvedPostalCode && !resolvedCity) {
          return current;
        }

        return {
          ...current,
          postalCode:
            resolvedPostalCode || current.postalCode,
          city: resolvedCity || current.city,
          country: String(
            resolved?.country || current.country || "FR",
          ).trim().toUpperCase(),
          lat: resolved?.lat ?? null,
          lng: resolved?.lng ?? null,
        };
      });
    } catch (error) {
      console.warn(
        "Resolution de l'adresse impossible",
        error,
      );
    }
  };

  const saveEdit = async () => {
    const validationError =
      getContactFormValidationError(
        editForm,
      );

    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (
      p.lifecycleStage === "client" &&
      editForm.lifecycleStage === "prospect"
    ) {
      toast.error(
        "Le retour Client vers Prospect n'est pas disponible."
      );
      return;
    }

    const payload = {
      first_name:
        String(editForm.firstName || "").trim(),
      last_name:
        String(editForm.lastName || "").trim(),
      email:
        String(editForm.email || "").trim(),
      phone:
        String(editForm.phone || "").trim(),
      company:
        String(editForm.company || "").trim(),
      position:
        String(editForm.position || "").trim(),
      address:
        String(editForm.address || "").trim(),
      postal_code:
        String(editForm.postalCode || "").trim(),
      city:
        String(editForm.city || "").trim(),
      country:
        String(editForm.country || "FR")
          .trim()
          .toUpperCase(),
      lat: editForm.lat ?? null,
      lng: editForm.lng ?? null,
    };

    setSaving(true);

    try {
      await prospectsApi.update(
        id,
        payload
      );

      if (
        p.lifecycleStage !== "client" &&
        editForm.lifecycleStage === "client"
      ) {
        await prospectsApi.convertToClient(
          id
        );
      }

      setEditOpen(false);
      await load();

      toast.success(
        "Informations du contact mises à jour"
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Modification impossible"
      );
    } finally {
      setSaving(false);
    }
  };


  const openContactDeleteModal = async () => {
    if (
      contactDeleting ||
      contactDeleteImpactLoading
    ) {
      return;
    }

    setContactDeleteImpactLoading(true);

    try {
      const impact =
        await prospectsApi.deleteImpact(
          id
        );

      setContactDeleteImpact(
        Array.isArray(
          impact?.dependencies
        )
          ? impact.dependencies
          : []
      );

      setContactDeleteModalOpen(true);

    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Impossible de préparer la suppression du contact"
      );

    } finally {
      setContactDeleteImpactLoading(false);
    }
  };


  const deleteContactRecord = async () => {
    if (contactDeleting) {
      return;
    }

    setContactDeleting(
      true
    );

    try {
      await prospectsApi.remove(
        id
      );

      setContactDeleteModalOpen(false);

      toast.success(
        "Contact supprimé"
      );

      navigate(
        "/app/commerce/prospects",
        {
          replace: true,
        }
      );

    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Suppression du contact impossible"
      );

    } finally {
      setContactDeleting(
        false
      );
    }
  };


  const updatePurchaseIntent = async () => {
    if (purchaseIntentSaving || !pendingPurchaseIntent) return;
    const purchaseIntent = pendingPurchaseIntent;

    setPurchaseIntentSaving(true);

    try {
      await prospectsApi.updatePurchaseIntent(id, purchaseIntent, purchaseIntentNote.trim());
      setPendingPurchaseIntent(null);
      setPurchaseIntentNote("");
      await load();
      toast.success("Intention d’achat mise à jour");
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
          "Mise à jour de l’intention impossible"
      );
    } finally {
      setPurchaseIntentSaving(false);
    }
  };


  const convertToClient = async () => {
    if (
      p?.lifecycleStage === "client"
    ) {
      toast.info("Ce contact est déjà client");
      return;
    }

    if (
      !window.confirm(
        "Convertir ce prospect en client ? Il sortira de la liste Prospects et apparaîtra dans Clients."
      )
    ) {
      return;
    }

    setCommercialSaving(true);

    try {
      await prospectsApi.convertToClient(id);
      await load();
      toast.success("Prospect converti en client");
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Conversion client impossible"
      );
    } finally {
      setCommercialSaving(false);
    }
  };




  const createQuoteFromProspect = async () => {
    const quantity =
      quoteForm.quantity === ""
        ? 1
        : Number(quoteForm.quantity);

    const unitPrice =
      quoteForm.amount === ""
        ? 0
        : Number(quoteForm.amount);

    if (
      !Number.isFinite(quantity) ||
      quantity <= 0
    ) {
      toast.error("Quantité invalide");
      return;
    }

    if (
      !Number.isFinite(unitPrice) ||
      unitPrice < 0
    ) {
      toast.error("Prix unitaire invalide");
      return;
    }

    const amount = quantity * unitPrice;

    setQuoteSaving(true);

    try {
      await quotesApi.create({
        prospect_id:
          p.lifecycleStage === "client"
            ? null
            : id,
        client_id:
          p.lifecycleStage === "client"
            ? id
            : null,
        title:
          quoteForm.title ||
          "Devis commercial",
        machine_catalog_id:
          quoteForm.machine_catalog_id ||
          null,
        machine_name:
          quoteForm.machine_name || "",
        amount,
        notes:
          quoteForm.notes || "",
        lines: [
          {
            description:
              quoteForm.machine_name ||
              quoteForm.title ||
              "Ligne de devis",
            quantity,
            unit_price: unitPrice,
            notes:
              quoteForm.notes || "",
          },
        ],
      });

      setQuoteForm({
        title: "Devis commercial",
        machine_catalog_id: "",
        machine_name: "",
        quantity: "1",
        amount: "",
        notes: "",
      });

      await loadQuotes();

      toast.success("Devis créé pour ce contact");
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Création du devis impossible"
      );
    } finally {
      setQuoteSaving(false);
    }
  };


  const runQuoteAction = async (
    successMessage,
    action
  ) => {
    setQuoteSaving(true);

    try {
      await action();
      await loadQuotes();
      await load();

      toast.success(successMessage);
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Action devis impossible"
      );
    } finally {
      setQuoteSaving(false);
    }
  };

  const downloadQuotePdf = async (quote) => {
    try {
      const blob = await quotesApi.downloadPdf(quote.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${quote.quoteNumber || quote.id || "devis"}.pdf`;
      link.click();

      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement PDF impossible");
    }
  };


  const viewSaleQuote = (sale) => {
    if (!sale?.quoteId) {
      toast.error("Aucun devis associé à cette vente");
      return;
    }

    setSaleQuotePreview({
      quoteId: sale.quoteId,
      title:
        sale.quoteNumber ||
        sale.quoteId ||
        "Devis",
    });
  };


  const openCommercialActivity = (
    activityType = "call"
  ) => {
    setCallOutcomeContext("");
    setNewInfo("");
    setNewInfoType(activityType);
    setNewInfoOutcome("");
    setNextActionType("followup");
    setNextActionChannel(
      activityType === "call"
        ? "phone"
        : "other"
    );
    setNextAction("");
    setNextActionAt("");
    setInfoModalOpen(true);
  };




  const resetComplementaryInfo = () => {
    setNewInfo("");
    setNewInfoOutcome("");
    setNextAction("");
    setNextActionAt("");
    setCallOutcomeContext("");
    setMeetingReportContext(null);
    meetingDictation.stop();
    setInfoModalOpen(false);
  };

  const saveOnlyCallResult = () => {
    resetComplementaryInfo();

    toast.success(
      "Résultat de l’appel conservé"
    );
  };

  const saveComplementaryInfo = async () => {
    const cleanContent =
      newInfo.trim();

    const cleanNextAction =
      nextAction.trim();

    if (
      newInfoType === "note" &&
      !cleanContent
    ) {
      toast.error(
        "Le contenu de la note est requis"
      );

      return;
    }

    if (
      Boolean(cleanNextAction) !==
      Boolean(nextActionAt)
    ) {
      toast.error(
        "Renseignez la prochaine action et sa date, ou laissez les deux champs vides"
      );

      return;
    }

    // Le résultat d’appel est déjà sauvegardé.
    // L’utilisateur peut fermer sans créer de seconde note.
    if (
      callOutcomeContext &&
      !cleanContent &&
      !cleanNextAction
    ) {
      saveOnlyCallResult();
      return;
    }

    let nextActionIso = null;

    if (nextActionAt) {
      const parsedDate =
        new Date(nextActionAt);

      if (
        Number.isNaN(
          parsedDate.getTime()
        )
      ) {
        toast.error(
          "Date de prochaine action invalide"
        );

        return;
      }

      nextActionIso =
        parsedDate.toISOString();
    }

    const fallbackContentByResult = {
      answered:
        "Compte rendu complémentaire après appel",
      no_answer:
        "Relance planifiée après un appel sans réponse",
      voicemail:
        "Relance planifiée après un message vocal",
      callback:
        "Rappel planifié après l’appel",
      not_interested:
        "Suivi après refus du prospect",
    };

    const contentToSave =
      cleanContent ||
      fallbackContentByResult[
        callOutcomeContext
      ] ||
      "Activité commerciale";

    const meetingNextAction =
      Boolean(cleanNextAction) &&
      nextActionType === "meeting";

    let createdMeetingId = "";

    setSaving(true);

    try {
      if (meetingNextAction) {
        const appointment =
          await appointmentsApi.create(
            id,
            {
              start_at:
                nextActionIso,
              duration_minutes:
                30,
              meeting_type:
                "online",
              subject:
                cleanNextAction,
              description:
                "",
              location:
                "",
            }
          );

        createdMeetingId =
          appointment?.id || "";
      }

      const shouldCreateNote =
        !meetingNextAction ||
        Boolean(cleanContent);

      if (shouldCreateNote) {
        await prospectsApi.addNote(
          id,
        {
          activity_type:
            newInfoType,
          content:
            contentToSave,
          activity_date:
            new Date().toISOString(),
          outcome:
            newInfoType === "note"
              ? null
              : (
                newInfoOutcome ||
                null
              ),
          next_action_type:
            !meetingNextAction &&
            cleanNextAction
              ? nextActionType
              : null,
          next_action_channel:
            !meetingNextAction &&
            cleanNextAction &&
            nextActionType === "followup"
              ? nextActionChannel
              : null,
          next_action:
            !meetingNextAction
              ? (
                cleanNextAction ||
                null
              )
              : null,
          next_action_at:
            !meetingNextAction
              ? nextActionIso
              : null,
          related_meeting_id:
            meetingReportContext
              ?.id ||
            null,
          related_meeting_source:
            meetingReportContext
              ?.source ||
            null,
        }
        );
      }

      resetComplementaryInfo();
      setPage(1);

      await load();

      toast.success(
        meetingNextAction
          ? (
            cleanContent
              ? "Compte rendu et rendez-vous enregistrés"
              : "Rendez-vous enregistré"
          )
          : (
            cleanNextAction
              ? "Prochaine action enregistrée"
              : "Compte rendu enregistré"
          )
      );
    } catch (error) {
      if (createdMeetingId) {
        try {
          await appointmentsApi.remove(
            createdMeetingId
          );
        } catch {
          // Le rendez-vous est un agrégat séparé :
          // une éventuelle erreur de rollback
          // ne masque pas l'erreur principale.
        }
      }

      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Ajout impossible"
      );
    } finally {
      setSaving(false);
    }
  };


  const saveQuickInformation = async () => {
    const content = newInfo.trim();

    if (!content) {
      toast.error(
        "Le contenu de la note est requis"
      );
      return;
    }

    if (content.length > 2000) {
      toast.error(
        "La note est limitée à 2 000 caractères"
      );
      return;
    }

    const allowedTypes = new Set([
      "note",
      "meeting",
      "call",
    ]);

    const activityType =
      allowedTypes.has(newInfoType)
        ? newInfoType
        : "note";

    setInformationSaving(true);

    try {
      await prospectsApi.addNote(
        id,
        {
          activity_type:
            activityType,
          content,
          activity_date:
            new Date().toISOString(),
          outcome: null,
          next_action_type: null,
          next_action: null,
          next_action_at: null,
          related_meeting_id:
            meetingReportContext
              ?.id ||
            null,
          related_meeting_source:
            meetingReportContext
              ?.source ||
            null,
        }
      );

      setNewInfo("");
      setNewInfoType("note");
      setMeetingReportContext(null);
      meetingDictation.stop();

      await load();

      toast.success(
        "Information enregistrée"
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Enregistrement impossible"
      );
    } finally {
      setInformationSaving(false);
    }
  };


  // ACTA_CONTACT_INFORMATION_NOTE_ACTIONS_V1
  const updateQuickInformation = async (
    note,
    content
  ) => {
    const cleanContent =
      String(content || "").trim();

    if (!note?.id || !cleanContent) {
      toast.error(
        "Le contenu de la note est requis"
      );
      return false;
    }

    if (cleanContent.length > 2000) {
      toast.error(
        "La note est limitée à 2 000 caractères"
      );
      return false;
    }

    setInformationSaving(true);

    try {
      await prospectsApi.updateNoteContent(
        note.id,
        cleanContent
      );

      await load();

      toast.success(
        "Note modifiée"
      );

      return true;
    } catch (error) {
      toast.error(
        formatApiError(
          error?.response?.data?.detail
        ) ||
        "Modification impossible"
      );

      return false;
    } finally {
      setInformationSaving(false);
    }
  };


  const deleteQuickInformation = async (
    note
  ) => {
    if (!note?.id) {
      return false;
    }

    const confirmed =
      window.confirm(
        "Supprimer cette note ?"
      );

    if (!confirmed) {
      return false;
    }

    setInformationSaving(true);

    try {
      await prospectsApi.removeNote(
        note.id
      );

      await load();

      toast.success(
        "Note supprimée"
      );

      return true;
    } catch (error) {
      toast.error(
        formatApiError(
          error?.response?.data?.detail
        ) ||
        "Suppression impossible"
      );

      return false;
    } finally {
      setInformationSaving(false);
    }
  };


  const openStandaloneMeeting = () => {
    const prospect = data?.prospect || data || {};

    const contactName =
      prospect.company ||
      [prospect.firstName, prospect.lastName]
        .filter(Boolean)
        .join(" ") ||
      "Contact";

    const suggestedStart =
      minimumMeetingInput(15);

    setStandaloneMeetingLinks(null);
    setStandaloneMeetingSaved(null);
    setStandaloneMeetingAppointmentId(null);
    setStandaloneMeetingForm({
      meetingType: "online",
      date: suggestedStart.date,
      start: suggestedStart.time,
      durationMinutes: "30",
      subject: `Rendez-vous ${contactName}`,
      notes: "",
      location:
        buildProspectMeetingAddress(
          prospect
        ),
    });
    setStandaloneMeetingOpen(true);
  };




  const editInternalAppointment = (
    appointment
  ) => {
    if (!appointment?.id) {
      toast.error(
        "Rendez-vous introuvable"
      );
      return;
    }

    const start =
      new Date(
        appointment.startAt
      );

    if (
      Number.isNaN(
        start.getTime()
      )
    ) {
      toast.error(
        "Date du rendez-vous invalide"
      );
      return;
    }

    const input =
      parisInputParts(start);

    setStandaloneMeetingLinks(null);
    setStandaloneMeetingShareLinks(null);
    setStandaloneMeetingSaved(null);

    setStandaloneMeetingAppointmentId(
      appointment.id
    );

    setStandaloneMeetingForm({
      meetingType:
        appointment.meetingType ||
        "online",

      date:
        input.date,

      start:
        input.time,

      durationMinutes:
        String(
          appointment.durationMinutes ||
          30
        ),

      subject:
        appointment.subject ||
        "",

      notes:
        appointment.description ||
        "",

      location:
        appointment.location ||
        "",
    });

    setStandaloneMeetingOpen(
      true
    );
  };


  const deleteInternalAppointment =
    async (appointment) => {
      if (!appointment?.id) {
        toast.error(
          "Rendez-vous introuvable"
        );
        return;
      }

      if (
        !window.confirm(
          `Supprimer le rendez-vous « ${
            appointment.subject ||
            "Rendez-vous"
          } » ?`
        )
      ) {
        return;
      }

      setStandaloneMeetingSaving(
        true
      );

      try {
        await appointmentsApi.remove(
          appointment.id
        );

        await Promise.all([
          load(),
          loadInternalAppointments(),
        ]);

        toast.success(
          "Rendez-vous supprimé"
        );

      } catch (error) {
        toast.error(
          formatApiError(
            error?.response?.data?.detail
          ) ||
          "Suppression du rendez-vous impossible"
        );

      } finally {
        setStandaloneMeetingSaving(
          false
        );
      }
    };


  const closeStandaloneMeeting = () => {
    if (standaloneMeetingSaving) return;

    setStandaloneMeetingOpen(false);
    setStandaloneMeetingLinks(null);
    setStandaloneMeetingShareLinks(null);
    setStandaloneMeetingSaved(null);
    setStandaloneMeetingAppointmentId(null);
  };

  // ACTA_MEETING_TYPE_CHANGE_V1
  const changeStandaloneMeetingType = (
    meetingType
  ) => {
    const prospect =
      data?.prospect || data || {};

    setStandaloneMeetingForm(
      (current) => ({
        ...current,

        // La date, l'heure, la durée et l'objet
        // sont volontairement conservés.
        meetingType,

        location:
          meetingType === "physical"
            ? (
                current.location ||
                buildProspectMeetingAddress(
                  prospect
                )
              )
            : current.location,
      })
    );
  };


  const saveStandaloneMeeting = async (event) => {
    event.preventDefault();

    const meeting = {
      ...standaloneMeetingForm,
      durationMinutes: Number(
        standaloneMeetingForm.durationMinutes
      ),
    };

    if (
      !meeting.date ||
      !meeting.start
    ) {
      toast.error(
        "Renseignez la date et l’heure."
      );
      return;
    }

    const subject =
      String(
        meeting.subject || ""
      ).trim();

    if (!subject) {
      toast.error(
        "Renseignez l’objet du rendez-vous."
      );
      return;
    }

    const start = new Date(
      `${meeting.date}T${meeting.start}`
    );

    if (
      Number.isNaN(
        start.getTime()
      )
    ) {
      toast.error(
        "Date de rendez-vous invalide."
      );
      return;
    }

    if (
      !Number.isFinite(
        meeting.durationMinutes
      ) ||
      meeting.durationMinutes <= 0
    ) {
      toast.error(
        "La durée du rendez-vous est invalide."
      );
      return;
    }

    const minimumInput =
      minimumMeetingInput(5);

    const selectedInput =
      `${meeting.date}T${meeting.start}`;

    if (
      selectedInput <
      minimumInput.key
    ) {
      toast.error(
        `Choisissez un créneau à partir du ${minimumInput.date} à ${minimumInput.time}.`
      );
      return;
    }

    const payload = {
      start_at:
        start.toISOString(),

      duration_minutes:
        meeting.durationMinutes,

      meeting_type:
        meeting.meetingType ||
        "online",

      subject,

      description:
        String(
          meeting.notes || ""
        ).trim(),

      location:
        meeting.meetingType ===
          "physical"
          ? String(
              meeting.location || ""
            ).trim()
          : "",
    };

    setStandaloneMeetingSaving(
      true
    );

    try {
      const wasEditing =
        Boolean(
          standaloneMeetingAppointmentId
        );

      const savedAppointment =
        standaloneMeetingAppointmentId
          ? await appointmentsApi.update(
              standaloneMeetingAppointmentId,
              payload
            )
          : await appointmentsApi.create(
              id,
              payload
            );

      await Promise.all([
        load(),
        loadInternalAppointments(),
      ]);

      setStandaloneMeetingAppointmentId(
        savedAppointment?.id ||
        standaloneMeetingAppointmentId ||
        null
      );

      const prospect =
        data?.prospect ||
        data ||
        {};

      setStandaloneMeetingLinks(
        buildMeetingCalendarLinks(
          meeting,
          prospect
        )
      );

      setStandaloneMeetingShareLinks(
        buildMeetingInvitationShareLinks(
          meeting,
          prospect
        )
      );

      setStandaloneMeetingSaved({
        ...meeting,
        subject,
        startIso:
          start.toISOString(),
      });

      toast.success(
        wasEditing
          ? "Rendez-vous modifié dans Acta"
          : "Rendez-vous enregistré dans Acta"
      );

    } catch (error) {
      toast.error(
        formatApiError(
          error?.response?.data?.detail
        ) ||
        "Enregistrement du rendez-vous impossible"
      );

    } finally {
      setStandaloneMeetingSaving(
        false
      );
    }
  };

  const copyStandaloneMeetingInvitation = async () => {
    const meeting = standaloneMeetingSaved || standaloneMeetingForm;
    const invitation = String(
      meeting.notes || ""
    ).trim();

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(invitation);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = invitation;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }
      toast.success("Invitation copiée");
    } catch {
      toast.error("Impossible de copier l’invitation");
    }
  };

  const editStandaloneMeeting = () => {
    setStandaloneMeetingLinks(null);
    setStandaloneMeetingShareLinks(null);

    toast(
      "Modifiez les informations puis enregistrez."
    );
  };

  // ACTA_STANDALONE_MEETING_DELETE_V1
  // ACTA_PROSPECT_ACTIVITY_SOFT_DELETE_UI_V1
  const deleteStandaloneMeeting = async () => {
    if (
      !standaloneMeetingAppointmentId
    ) {
      toast.error(
        "Rendez-vous introuvable"
      );
      return;
    }

    if (
      !window.confirm(
        "Supprimer ce rendez-vous ?"
      )
    ) {
      return;
    }

    setStandaloneMeetingSaving(
      true
    );

    try {
      await appointmentsApi.remove(
        standaloneMeetingAppointmentId
      );

      await Promise.all([
        load(),
        loadInternalAppointments(),
      ]);

      closeStandaloneMeeting();

      toast.success(
        "Rendez-vous supprimé"
      );

    } catch (error) {
      toast.error(
        formatApiError(
          error?.response?.data?.detail
        ) ||
        "Suppression du rendez-vous impossible"
      );

    } finally {
      setStandaloneMeetingSaving(
        false
      );
    }
  };

  const deleteNote = async (noteId) => {
    if (
      !window.confirm(
        "Masquer cette activité de l’historique ?\n\n" +
        "La trace restera conservée pour l’audit et l’analyse."
      )
    ) {
      return;
    }

    try {
      await prospectsApi.removeNote(
        noteId
      );
      await load();

      toast.success(
        "Activité masquée de l’historique"
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
        "Masquage de l’activité impossible"
      );
    }
  };

  const changeStatus = async (status) => {
    try {
      await prospectsApi.pipelineTransition(id, {
        commercial_status: status,
      });
      await load();
      toast.success("Statut commercial mis à jour");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

    const openClientOpportunityModal = () => {
    setClientOpportunityModalOpen(true);
  };

  const closeClientOpportunityModal = () => {
    setClientOpportunityModalOpen(false);
  };


const sendCommercialMessage = async () => {
    const content = reply.trim();
    if (!content || !selectedShareId) return;

    try {
      const created = await sharesApi.sendExchangeMessage(selectedShareId, { content });
      setExchangeMessages((current) => [...current, created]);
      setReply("");
      toast.success("Message envoyé");
    } catch {
      toast.error("Message impossible à envoyer");
    }
  };

  const addSlot = () => {
    if (slots.length >= 3) return;
    setSlots((current) => [
      ...current,
      {
        id: `slot_${Date.now()}`,
        date: "",
        start: "",
        durationMinutes: "30",
        locationType: "online",
        location: "",
      },
    ]);
  };

  const updateSlot = (slotId, key, value) => {
    setSlots((current) => current.map((slot) => (slot.id === slotId ? { ...slot, [key]: value } : slot)));
  };

  const removeSlot = (slotId) => {
    if (slots.length <= 1) return;
    setSlots((current) => current.filter((slot) => slot.id !== slotId));
  };

  // ACTA_MEETING_SELECT_HANDLER_V2
  const selectMeetingSlot = async (meeting, selectedSlotIndex) => {
    if (!selectedShareId || !meeting?.id) {
      return;
    }

    const selectedSlot =
      meeting.slots?.[selectedSlotIndex];

    if (!selectedSlot) {
      toast.error("Le créneau sélectionné est introuvable.");
      return;
    }

    const dateLabel =
      formatMeetingDateFr(selectedSlot.date) ||
      selectedSlot.date ||
      "la date choisie";

    const timeLabel =
      selectedSlot.start || "l’heure choisie";

    const confirmed = window.confirm(
      [
        `Confirmer le rendez-vous du ${dateLabel} à ${timeLabel} ?`,
        "",
        selectedSlot.location
          ? `Lieu : ${selectedSlot.location}`
          : "",
        "",
        "Cette action validera définitivement ce créneau dans AlmaCRM.",
      ]
        .filter(Boolean)
        .join("\n")
    );

    if (!confirmed) {
      return;
    }

    try {
      await sharesApi.selectExchangeMeeting(
        selectedShareId,
        meeting.id,
        {
          selectedSlotIndex,
        }
      );

      await loadExchange(selectedShareId);

      toast.success(
        "Rendez-vous confirmé. Finalisez l’invitation dans Outlook ou Gmail."
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error?.response?.data?.detail
        ) ||
          "Impossible de confirmer ce rendez-vous."
      );
    }
  };

  const proposeMeetings = async () => {
    const validSlots = slots
      .slice(0, 1)
      .map((slot) => ({
        date: String(slot.date || "").trim(),
        start: String(slot.start || "").trim(),
        durationMinutes: Number(
          slot.durationMinutes || 30
        ),
        locationType: String(
          slot.locationType || "online"
        ).trim(),
        location: String(
          slot.location || ""
        ).trim(),
      }))
      .filter(
        (slot) =>
          slot.date &&
          slot.start &&
          slot.durationMinutes &&
          (
            slot.locationType !== "physical" ||
            slot.location
          )
      );

    if (!selectedShareId) return toast.error("Sélectionnez un partage");
    if (!validSlots.length) {
      return toast.error(
        "Ajoutez au moins un créneau avec date, heure, durée et lieu"
      );
    }

    try {
      if (counterMeetingId) {
        await sharesApi.counterExchangeMeeting(
          selectedShareId,
          counterMeetingId,
          {
            slots: validSlots,
          }
        );
        toast.success(
          "Contre-proposition envoyée"
        );
        setCounterMeetingId(null);
      } else if (editingMeetingId) {
        await sharesApi.updateExchangeMeeting(
          selectedShareId,
          editingMeetingId,
          {
            slots: validSlots,
          }
        );
        toast.success("Rendez-vous modifié");
        setEditingMeetingId(null);
      } else {
        await sharesApi.proposeExchangeMeetings(
          selectedShareId,
          {
            slots: validSlots,
          }
        );
        toast.success("Rendez-vous proposés");
      }
      setSlots([
        {
          id: "slot_1",
          date: "",
          start: "",
          durationMinutes: "30",
          locationType: "online",
          location: "",
        },
      ]);
      await loadExchange(selectedShareId);
    } catch {
      toast.error("Rendez-vous impossibles à enregistrer");
    }
  };

  // ACTA_MEETING_INTERNAL_DECISION_HANDLERS_V1
  const confirmAcceptedMeeting = async (
    meeting
  ) => {
    if (!selectedShareId || !meeting?.id) {
      return;
    }

    try {
      await sharesApi.confirmExchangeMeeting(
        selectedShareId,
        meeting.id
      );

      await loadExchange(selectedShareId);

      toast.success(
        "Rendez-vous confirmé. Finalisez l’invitation dans Outlook ou Gmail."
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error?.response?.data?.detail
        ) ||
          "Confirmation du rendez-vous impossible"
      );
    }
  };

  const refuseProspectMeeting = async (
    meeting
  ) => {
    if (!selectedShareId || !meeting?.id) {
      return;
    }

    try {
      await sharesApi.refuseExchangeMeeting(
        selectedShareId,
        meeting.id
      );

      await loadExchange(selectedShareId);

      toast.success(
        "Proposition refusée"
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error?.response?.data?.detail
        ) ||
          "Refus impossible"
      );
    }
  };

  const counterProspectMeeting = (
    meeting
  ) => {
    setEditingMeetingId(null);
    setCounterMeetingId(meeting.id);

    const firstSlot =
      meeting.slots?.[0] || {};

    setSlots([
      {
        id: "slot_1",
        date: firstSlot.date || "",
        start: firstSlot.start || "",
        durationMinutes: String(
          firstSlot.durationMinutes || 30
        ),
        locationType:
          firstSlot.locationType || "online",
        location:
          firstSlot.locationType === "physical"
            ? firstSlot.location || ""
            : "",
      },
    ]);

    toast.info(
      "Modifiez le créneau puis envoyez la contre-proposition"
    );
  };

  const editMeeting = (meeting) => {
    const nextSlots = (meeting.slots || []).map((slot, index) => ({
      id: `slot_${index + 1}`,
      date: slot.date || "",
      start: slot.start || "",
      durationMinutes: String(
        slot.durationMinutes || 30
      ),
      locationType:
        slot.locationType || "online",
      location: slot.location || "",
    }));
    if (!nextSlots.length) return toast.error("Aucun rendez-vous à modifier");
    setEditingMeetingId(meeting.id);
    setSlots(nextSlots);
    toast.info("Modifiez le rendez-vous puis enregistrez");
  };

  const deleteMeeting = async (meeting) => {
    if (!selectedShareId || !meeting?.id) return;
    if (!window.confirm("Supprimer cette proposition de rendez-vous ?")) return;
    try {
      await sharesApi.deleteExchangeMeeting(selectedShareId, meeting.id);
      if (editingMeetingId === meeting.id) {
        setEditingMeetingId(null);
        setSlots([
        {
          id: "slot_1",
          date: "",
          start: "",
          durationMinutes: "30",
          locationType: "online",
          location: "",
        },
      ]);
      }
      await loadExchange(selectedShareId);
      toast.success("Rendez-vous supprimé");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const tabs = [
    { id: "sales", label: "Machines", icon: Wrench },
    { id: "information", label: "Informations", icon: Note },
    { id: "comments", label: "Activité", icon: Note },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "messages", label: "Messages", icon: ChatCircleText },
    { id: "meetings", label: "Rendez-vous", icon: Calendar },
    ...(canAccessWorkshops ? [{ id: "workshops", label: "Workshops", icon: Calendar }] : []),
  ];

  const renderContent = () => {
    if (activeTab === "sales") {
      return (
        <ContactMachinesPanel
          prospect={p}
          prospectId={id}
          user={user}
        />
      );
    }


    if (activeTab === "information") {
      return (
        <ContactInformationPanel
          notes={data?.notes || []}
          value={newInfo}
          type={newInfoType}
          saving={informationSaving}
          meetingReportMode={
            Boolean(
              meetingReportContext
            )
          }
          dictationSupported={
            meetingDictation.supported
          }
          dictationListening={
            meetingDictation.listening
          }
          onDictationToggle={() => {
            if (
              meetingDictation.listening
            ) {
              meetingDictation.stop();
            } else {
              meetingDictation.start();
            }
          }}
          onChange={setNewInfo}
          onTypeChange={setNewInfoType}
          onSave={saveQuickInformation}
          onUpdateNote={
            updateQuickInformation
          }
          onDeleteNote={
            deleteQuickInformation
          }
        />
      );
    }

    if (activeTab === "quotes") {
      return (
        <>
          <QuotesPanel
            prospect={p}
            quotes={quotes}
            loading={quotesLoading}
            createForm={
              <QuoteCreateForm
                defaultProspectId={id}
                lockedProspect={p}
                onCreated={async () => {
                  await loadQuotes();
                  await load();
                }}
              />
            }
            saving={quoteSaving}
            form={quoteForm}
            machineCatalog={machineCatalog}
            isAdmin={user?.role === "admin"}
            onChange={(field, value) =>
              setQuoteForm((current) => ({
                ...current,
                [field]: value,
              }))
            }
            onCreate={createQuoteFromProspect}
            onSubmit={(quote) =>
              runQuoteAction(
                "Devis soumis à validation admin",
                () => quotesApi.submit(quote.id)
              )
            }
            onApprove={(quote) =>
              runQuoteAction(
                "Devis validé",
                () => quotesApi.approve(quote.id)
              )
            }
            onReject={(quote) => {
              const reason =
                window.prompt("Motif du rejet du devis");

              if (!reason) {
                return;
              }

              runQuoteAction(
                "Devis rejeté",
                () => quotesApi.reject(quote.id, reason)
              );
            }}
            onDownloadPdf={downloadQuotePdf}
            onSend={(quote) =>
              runQuoteAction(
                "Devis envoyé au client",
                () => quotesApi.send(quote.id)
              )
            }
            onAccept={(quote) =>
              runQuoteAction(
                "Devis accepté",
                () => quotesApi.accept(quote.id)
              )
            }
            onRefuse={(quote) => {
              setQuoteRefusalReason("");
              setQuoteRefusalModal(quote);
            }}
          />

          {quoteRefusalModal && (
            <div
              className="acta-modal-overlay fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4"
              data-testid="prospect-quote-refusal-modal"
              onClick={() => {
                if (!quoteSaving) {
                  setQuoteRefusalModal(null);
                  setQuoteRefusalReason("");
                }
              }}
            >
              <form
                className="acta-modal-surface w-full max-w-lg rounded-3xl p-5 sm:p-6"
                data-testid="prospect-quote-refusal-form"
                onClick={(event) => event.stopPropagation()}
                onSubmit={async (event) => {
                  event.preventDefault();

                  const reason =
                    quoteRefusalReason.trim();

                  if (!reason) {
                    toast.error(
                      "Le motif du refus client est obligatoire."
                    );
                    return;
                  }

                  setQuoteSaving(true);

                  try {
                    await quotesApi.refuse(
                      quoteRefusalModal.id,
                      reason
                    );

                    toast.success(
                      "Refus client enregistré."
                    );

                    setQuoteRefusalModal(null);
                    setQuoteRefusalReason("");

                    await loadQuotes();
                    await load();
                  } catch (error) {
                    toast.error(
                      formatApiError(
                        error.response?.data?.detail
                      ) ||
                        error.message ||
                        "Impossible d’enregistrer le refus client."
                    );
                  } finally {
                    setQuoteSaving(false);
                  }
                }}
              >
                <div className="border-b border-[var(--do-border)] pb-4">
                  <div className="label-eyebrow">
                    Réponse du client
                  </div>

                  <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-ink)]">
                    Enregistrer le refus client
                  </h2>

                  <p className="mt-2 text-sm text-[var(--do-muted)]">
                    {quoteRefusalModal.quoteNumber ||
                      quoteRefusalModal.id}
                    {" — "}
                    {quoteRefusalModal.title ||
                      "Devis commercial"}
                  </p>
                </div>

                <label className="mt-5 block">
                  <span className="text-xs font-bold text-[var(--do-muted)]">
                    Motif obligatoire
                  </span>

                  <textarea
                    rows={4}
                    value={quoteRefusalReason}
                    onChange={(event) =>
                      setQuoteRefusalReason(
                        event.target.value
                      )
                    }
                    placeholder="Indiquez la raison communiquée par le client…"
                    className="mt-2 w-full resize-y rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)]"
                    data-testid="prospect-quote-refusal-reason"
                    autoFocus
                  />
                </label>

                <div className="mt-6 flex flex-col-reverse gap-3 border-t border-[var(--do-border)] pt-4 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    disabled={quoteSaving}
                    onClick={() => {
                      setQuoteRefusalModal(null);
                      setQuoteRefusalReason("");
                    }}
                    className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 py-3 text-sm font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-solid)] disabled:opacity-50"
                  >
                    Annuler
                  </button>

                  <button
                    type="submit"
                    disabled={
                      quoteSaving ||
                      !quoteRefusalReason.trim()
                    }
                    className="rounded-2xl bg-[var(--do-accent)] px-5 py-3 text-sm font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)] disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="prospect-quote-refusal-submit"
                  >
                    {quoteSaving
                      ? "Enregistrement…"
                      : "Confirmer le refus"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      );
    }

    if (activeTab === "documents") {
      return (
        <DocumentsPanel
          documents={resolvedContactDocuments}
          page={page}
          onPage={setPage}
          onOpen={(document) =>
            setDocumentPreview(document.sourceDocument)
          }
        />
      );
    }

    if (activeTab === "comments") {
      return (
        <TimelinePanel
          items={filteredTimeline}
          allItems={unifiedTimeline}
          filter={activityFilter}
          onFilter={(value) => {
            setActivityFilter(value);
            setPage(1);
          }}
          page={page}
          onPage={setPage}
          onAdd={() =>
            openCommercialActivity("note")
          }
          onShare={() =>
            setDocPicker(true)
          }
          onMeeting={() => {
            setActiveTab("meetings");
            setPage(1);
          }}
          activityStateSaving={
            activityStateSaving
          }
          onHideItem={async (item) => {
            const confirmed =
              window.confirm(
                "Masquer cette activité de votre historique ? Elle restera conservée en base de données."
              );

            if (!confirmed) {
              return;
            }

            const hidden =
              await updateActivityState(
                item,
                {
                  hidden: true,
                  read: true,
                }
              );

            if (hidden) {
              toast.success(
                "Activité masquée de votre historique"
              );
            }
          }}
          onOpenItem={async (item) => {
            if (
              item.requiresAction &&
              (
                item.category ===
                  "messages" ||
                item.category ===
                  "documents"
              )
            ) {
              await updateActivityState(
                item,
                {
                  read: true,
                }
              );
            }
            if (item.shareId) {
              setSelectedShareId(
                item.shareId
              );
            }

            if (
              item.actionType ===
                "categorize_call" &&
              item.id
            ) {
              setActiveTab("comments");
              setPage(1);

              navigate(
                `/app/commerce/prospects/${encodeURIComponent(
                  id
                )}?tab=comments&callOutcome=${encodeURIComponent(
                  item.id
                )}`
              );

              return;
            }

            if (
              item.actionType === "mail" &&
              item.mailMessageId
            ) {
              navigate(
                `/app/mail?message=${encodeURIComponent(
                  item.mailMessageId
                )}`
              );
              return;
            }

            if (
              item.actionType === "reply"
            ) {
              setActiveTab("messages");
              setPage(1);
              return;
            }

            if (
              item.actionType === "meeting"
            ) {
              setActiveTab("meetings");
              setPage(1);
              return;
            }

            if (
              item.actionType === "share" &&
              item.shareId
            ) {
              navigate(
                `/app/commerce/stats?share_id=${encodeURIComponent(
                  item.shareId
                )}`
              );
              return;
            }

            if (
              item.actionType === "share"
            ) {
              toast.error(
                "Les statistiques de ce partage sont indisponibles"
              );
            }
          }}
        />
      );
    }

    if (activeTab === "workshops") {
      return <ContactWorkshopsPanel contactId={id} />;
    }

    if (activeTab === "messages") {
      return (
        <MessagesPanel
          shares={exchangeShares}
          selectedShareId={selectedShareId}
          onSelectShare={setSelectedShareId}
          messages={exchangeMessages}
          highlightedMessageId={requestedMessageId}
          reply={reply}
          onReply={setReply}
          onSend={sendCommercialMessage}
        />
      );
    }

    return (
      <div className="space-y-5">
        <InternalAppointmentsPanel
          appointments={
            internalAppointments
          }
          loading={
            internalAppointmentsLoading
          }
          saving={
            standaloneMeetingSaving
          }
          onCreate={
            openStandaloneMeeting
          }
          onEdit={
            editInternalAppointment
          }
          onDelete={
            deleteInternalAppointment
          }
        />

        <MeetingsPanel
          shares={exchangeShares}
          selectedShareId={selectedShareId}
          onSelectShare={setSelectedShareId}
          meetings={exchangeMeetings}
          slots={slots}
          onAddSlot={addSlot}
          onUpdateSlot={updateSlot}
          onRemoveSlot={removeSlot}
          onPropose={proposeMeetings}
          editingMeetingId={editingMeetingId}
          onEditMeeting={editMeeting}
          onDeleteMeeting={deleteMeeting}
          prospect={p}
          onSelectSlot={selectMeetingSlot}
          onConfirmAccepted={
            confirmAcceptedMeeting
          }
          onRefuseProspect={
            refuseProspectMeeting
          }
          onCounterProspect={
            counterProspectMeeting
          }
          counterMeetingId={counterMeetingId}
        />
      </div>
    );
  };

  return (
    <div className="max-w-[1600px] space-y-4 p-3 sm:p-4 lg:p-5" data-testid="prospect-detail-page">
            {/* Nouvelle opportunité */}
      {pendingPurchaseIntent !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="presentation">
          <div role="dialog" aria-modal="true" aria-labelledby="purchase-intent-dialog-title" className="w-full max-w-md rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 shadow-xl">
            <h2 id="purchase-intent-dialog-title" className="font-heading text-lg font-black text-[var(--do-ink)]">Confirmer le changement d’intention d’achat</h2>
            <p className="mt-3 text-sm text-[var(--do-ink)]">
              <PurchaseIntentBadge value={p?.purchaseIntent} /> <span aria-hidden="true">→</span> <PurchaseIntentBadge value={pendingPurchaseIntent} />
            </p>
            <label htmlFor="purchase-intent-note" className="mt-4 block text-sm font-bold text-[var(--do-ink)]">Note facultative</label>
            <textarea id="purchase-intent-note" value={purchaseIntentNote} onChange={(event) => setPurchaseIntentNote(event.target.value)} maxLength={2000} rows={3} className="mt-2 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3 text-[var(--do-ink)]" />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" disabled={purchaseIntentSaving} onClick={() => { setPendingPurchaseIntent(null); setPurchaseIntentNote(""); }} className="rounded-xl border border-[var(--do-border)] px-4 py-2 text-[var(--do-ink)]">Annuler</button>
              <button type="button" disabled={purchaseIntentSaving} onClick={updatePurchaseIntent} className="rounded-xl bg-[var(--do-accent)] px-4 py-2 font-bold text-[var(--do-on-accent)]">Valider</button>
            </div>
          </div>
        </div>
      )}
      <OpportunityCreateModal
        open={clientOpportunityModalOpen}
        lockedProspect={p}
        onClose={
          closeClientOpportunityModal
        }
        onCreated={async () => {
          await load();
          setActiveTab("sales");
        }}
      />

{standaloneMeetingOpen && (
        <div
          className="acta-modal-overlay fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4"
          data-testid="standalone-meeting-modal"
        >
          <form
            onSubmit={saveStandaloneMeeting}
            onClick={(event) =>
              event.stopPropagation()
            }
            className="acta-modal-surface standalone-meeting-sheet flex max-h-[calc(100dvh-24px)] w-full max-w-2xl flex-col overflow-hidden rounded-3xl sm:max-h-[calc(100dvh-32px)]"
            role="dialog"
            aria-modal="true"
            aria-label="Rendez-vous commercial"
          >
            <div className="standalone-meeting-sheet-header sticky top-0 z-10 flex shrink-0 items-start justify-between gap-4 border-b border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 sm:p-6">
              <div>
                <div className="label-eyebrow">
                  Agenda commercial
                </div>

                <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-ink)]">
                  {standaloneMeetingLinks
                    ? "Ajouter au calendrier"
                    : "Créer un rendez-vous"}
                </h2>
              </div>

              <button
                type="button"
                onClick={closeStandaloneMeeting}
                className="h-10 w-10 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-xl font-black text-[var(--do-muted)]"
              >
                ×
              </button>
            </div>

            <div className="standalone-meeting-sheet-body min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">
            {!standaloneMeetingLinks ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Type
                    </span>
                    <select
                      value={standaloneMeetingForm.meetingType}
                      onChange={(event) =>
                        changeStandaloneMeetingType(
                          event.target.value
                        )
                      }
                      className="mt-2 min-h-[46px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4"
                    >
                      <option value="online">
                        En ligne
                      </option>
                      <option value="physical">
                        Physique
                      </option>
                    </select>
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Durée
                    </span>
                    <select
                      value={standaloneMeetingForm.durationMinutes}
                      onChange={(event) =>
                        setStandaloneMeetingForm(
                          (current) => ({
                            ...current,
                            durationMinutes:
                              event.target.value,
                          })
                        )
                      }
                      className="mt-2 min-h-[46px] w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 heure</option>
                      <option value="90">1 h 30</option>
                    </select>
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Date *
                    </span>
                    <input
                      type="date"
                      required
                      min={minimumMeetingInput(5).date}
                      value={standaloneMeetingForm.date}
                      onChange={(event) =>
                        setStandaloneMeetingForm(
                          (current) => ({
                            ...current,
                            date: event.target.value,
                          })
                        )
                      }
                      className="prospect-mobile-datetime mt-2 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-base text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)] sm:text-sm"
                      data-testid="standalone-meeting-date"
                    />
                  </label>

                  <label>
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Heure *
                    </span>
                    <input
                      type="time"
                      required
                      min={
                        standaloneMeetingForm.date ===
                        localDateInputValue()
                          ? minimumMeetingInput(5).time
                          : undefined
                      }
                      value={standaloneMeetingForm.start}
                      onChange={(event) =>
                        setStandaloneMeetingForm(
                          (current) => ({
                            ...current,
                            start: event.target.value,
                          })
                        )
                      }
                      className="prospect-mobile-datetime mt-2 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-base text-[var(--do-ink)] outline-none focus:border-[var(--do-border-strong)] sm:text-sm"
                      data-testid="standalone-meeting-time"
                    />
                  </label>

                  <label className="sm:col-span-2">
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Objet *
                    </span>
                    <input
                      type="text"
                      required
                      value={standaloneMeetingForm.subject}
                      onChange={(event) =>
                        setStandaloneMeetingForm(
                          (current) => ({
                            ...current,
                            subject: event.target.value,
                          })
                        )
                      }
                      className="mt-2 min-h-[46px] w-full rounded-xl border border-[var(--do-border)] px-4"
                    />
                  </label>

                  {standaloneMeetingForm.meetingType ===
                    "physical" && (
                    <label className="sm:col-span-2">
                      <span className="text-xs font-bold text-[var(--do-muted)]">
                        Adresse du rendez-vous
                      </span>
                      <input
                        type="text"
                        value={standaloneMeetingForm.location}
                        onChange={(event) =>
                          setStandaloneMeetingForm(
                            (current) => ({
                              ...current,
                              location:
                                event.target.value,
                            })
                          )
                        }
                        placeholder="Adresse complète du rendez-vous"
                        className="mt-2 min-h-[46px] w-full rounded-xl border border-[var(--do-border)] px-4"
                        data-testid="standalone-meeting-location"
                      />
                      <p className="mt-1 text-xs text-[var(--do-muted)]">
                        Cette adresse sera préremplie dans Google Agenda et Outlook.
                      </p>
                    </label>
                  )}

                  {standaloneMeetingForm.meetingType ===
                    "online" && (
                    <div className="sm:col-span-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-xs text-[var(--do-ink)]">
                      Le rendez-vous sera créé sans lieu. Vous pourrez ajouter Google Meet ou Microsoft Teams depuis votre agenda.
                    </div>
                  )}

                  <label className="sm:col-span-2">
                    <span className="text-xs font-bold text-[var(--do-muted)]">
                      Description du rendez-vous
                    </span>
                    <textarea
                      rows={4}
                      value={standaloneMeetingForm.notes}
                      onChange={(event) =>
                        setStandaloneMeetingForm(
                          (current) => ({
                            ...current,
                            notes: event.target.value,
                          })
                        )
                      }
                      placeholder="Ce texte sera utilisé comme description dans l’invitation calendrier."
                      className="mt-2 w-full rounded-xl border border-[var(--do-border)] px-4 py-3"
                    />
                  </label>
                </div>

                <div className="standalone-meeting-sheet-footer sticky bottom-0 z-10 -mx-4 mt-5 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 pt-4 sm:-mx-6 sm:px-6">
                  <button
                    type="submit"
                    disabled={standaloneMeetingSaving}
                    className="min-h-[48px] w-full rounded-xl bg-[var(--do-accent)] px-5 py-3 font-black text-[var(--do-on-accent)] disabled:opacity-50"
                    data-testid="standalone-meeting-submit"
                  >
                    {standaloneMeetingSaving
                      ? "Enregistrement…"
                      : "Enregistrer et choisir le calendrier"}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4" data-testid="standalone-meeting-confirmation">
                <div className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
                  <div className="label-eyebrow">Détails du rendez-vous</div>
                  <div className="mt-2 font-heading text-lg font-black text-[var(--do-ink)]">
                    {standaloneMeetingSaved?.subject}
                  </div>
                  <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                    <div><dt className="text-xs font-bold text-[var(--do-muted)]">Date</dt><dd className="mt-1 font-black text-[var(--do-ink)]">{formatMeetingDateFr(standaloneMeetingSaved?.date)}</dd></div>
                    <div><dt className="text-xs font-bold text-[var(--do-muted)]">Heure</dt><dd className="mt-1 font-black text-[var(--do-ink)]">{standaloneMeetingSaved?.start}</dd></div>
                    <div><dt className="text-xs font-bold text-[var(--do-muted)]">Durée</dt><dd className="mt-1 font-black text-[var(--do-ink)]">{standaloneMeetingSaved?.durationMinutes} minutes</dd></div>
                    <div><dt className="text-xs font-bold text-[var(--do-muted)]">Type</dt><dd className="mt-1 font-black text-[var(--do-ink)]">{standaloneMeetingSaved?.meetingType === "physical" ? "Physique" : "En ligne"}</dd></div>
                    {standaloneMeetingSaved?.meetingType === "physical" && (
                      <div className="sm:col-span-2">
                        <dt className="text-xs font-bold text-[var(--do-muted)]">Adresse</dt>
                        <dd className="mt-1 font-black text-[var(--do-ink)]">
                          {standaloneMeetingSaved?.location || "—"}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                {standaloneMeetingShareLinks && (
                  <section
                    className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4"
                    data-testid="standalone-meeting-client-share"
                  >
                    <h3 className="font-heading text-base font-black text-[var(--do-ink)]">
                      Partager au client
                    </h3>

                    <p className="mt-1 text-xs text-[var(--do-muted)]">
                      L’invitation est préremplie avec les informations du rendez-vous.
                    </p>

                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <a
                        href={standaloneMeetingShareLinks.gmail}
                        target="_blank"
                        rel="noreferrer"
                        className="flex min-h-[50px] items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)]"
                        data-testid="standalone-meeting-share-gmail"
                      >
                        Gmail
                      </a>

                      <a
                        href={standaloneMeetingShareLinks.mailto}
                        className="flex min-h-[50px] items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)]"
                        data-testid="standalone-meeting-share-email"
                      >
                        Outlook / Email
                      </a>

                      <a
                        href={standaloneMeetingShareLinks.whatsapp}
                        target="_blank"
                        rel="noreferrer"
                        className="flex min-h-[50px] items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)]"
                        data-testid="standalone-meeting-share-whatsapp"
                      >
                        WhatsApp
                      </a>
                    </div>
                  </section>
                )}

                <section
                  className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4"
                  data-testid="standalone-meeting-calendar-share"
                >
                  <h3 className="font-heading text-base font-black text-[var(--do-ink)]">
                    Ajouter à l’agenda
                  </h3>

                  <p className="mt-1 text-xs text-[var(--do-muted)]">
                    L’événement s’ouvre directement prérempli avec le client, la date, l’heure, la durée, l’objet, la description et l’adresse.
                  </p>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <a
                      href={standaloneMeetingLinks.google}
                      target="_blank"
                      rel="noreferrer"
                      className="flex min-h-[52px] items-center justify-center rounded-xl bg-[var(--do-accent)] px-5 font-black text-[var(--do-on-accent)]"
                      data-testid="standalone-meeting-google"
                    >
                      Google Agenda
                    </a>

                    <a
                      href={standaloneMeetingLinks.outlook}
                      target="_blank"
                      rel="noreferrer"
                      className="flex min-h-[52px] items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-5 font-black text-[var(--do-ink)]"
                      data-testid="standalone-meeting-outlook"
                    >
                      Outlook Agenda
                    </a>
                  </div>
                </section>

                <div className="grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={copyStandaloneMeetingInvitation}
                    className="min-h-[52px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 font-black text-[var(--do-ink)]"
                    data-testid="standalone-meeting-copy"
                  >
                    Copier l’invitation
                  </button>

                  <button
                    type="button"
                    onClick={editStandaloneMeeting}
                    className="min-h-[52px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 font-black text-[var(--do-ink)]"
                    data-testid="standalone-meeting-edit"
                  >
                    Modifier
                  </button>
                </div>

                <button type="button" onClick={closeStandaloneMeeting} className="min-h-[50px] w-full rounded-xl bg-[var(--do-ink)] px-5 font-black text-[var(--do-on-accent)]" data-testid="standalone-meeting-finish">Terminer</button>
              </div>
            )}
            </div>
          </form>
        </div>
      )}

      <ProspectProfileHeader
        prospect={p}
        canAssignCommercial={
          canAssignCommercial
        }
        commercialUsers={
          commercialUsers
        }
        commercialUsersLoading={
          commercialUsersLoading
        }
        assigningCommercial={
          assigningCommercial
        }
        onAssignCommercial={
          assignCommercial
        }
        collaborators={
          collaborators
        }
        collaboratorCandidates={
          collaboratorCandidates
        }
        collaboratorIdToAdd={
          collaboratorIdToAdd
        }
        collaboratorsLoading={
          collaboratorsLoading
        }
        collaboratorsSaving={
          collaboratorsSaving
        }
        onCollaboratorSelect={
          setCollaboratorIdToAdd
        }
        onCollaboratorAdd={
          addCollaborator
        }
        onCollaboratorRemove={
          removeCollaborator
        }
        onEdit={openEdit}
        onPurchaseIntentChange={(intent) => {
          if (normalizePurchaseIntent(intent) !== normalizePurchaseIntent(p?.purchaseIntent)) {
            setPendingPurchaseIntent(intent);
            setPurchaseIntentNote("");
          }
        }}
        purchaseIntentSaving={purchaseIntentSaving}
        onDelete={openContactDeleteModal}
        deleting={
          contactDeleting ||
          contactDeleteImpactLoading
        }
        onShare={() =>
          setDocPicker(true)
        }
        onMeeting={openStandaloneMeeting}
        onEmail={() => {
          if (!p.email) {
            toast.error("Aucune adresse e-mail renseignée");
            return;
          }

          navigate(
            buildMailComposePath({
              to: p.email,
              prospectId: id,
            })
          );
        }}
        onCreateQuote={() => {
          navigate(`/app/commerce/pipeline?action=new-quote&contactId=${encodeURIComponent(id)}`);
        }}
        onActivityRefresh={load}
        onCallFollowup={() =>
          navigate(
            `/app/commerce/agenda?action=new-followup&prospectId=${encodeURIComponent(id)}`
          )
        }
        onCallNewAction={() =>
          navigate(
            `/app/commerce/agenda?action=new-action&prospectId=${encodeURIComponent(id)}`
          )
        }
        onCallNote={() => {
          setActiveTab("information");
          setPage(1);
          setNewInfoType("note");
        }}
        requestedCallOutcomeId={
          requestedCallOutcomeId
        }
        requestedAgentCall={
          requestedAgentCall
        }
        onConsumeAgentCall={
          consumeRequestedAgentCall
        }
        requestedCallActivity={
          (data?.notes || []).find(
            (note) =>
              note.id ===
                requestedCallOutcomeId &&
              (
                note.activityType ||
                note.activity_type
              ) === "call" &&
              Boolean(note.callChannel || note.call_channel) &&
              !(
                note.callResultCode ||
                note.call_result_code
              )
          ) || null
        }
      />

      {/* Desktop : navigation historique conservée. */}
      <section
        className="au-card hidden overflow-hidden md:block"
        data-testid="prospect-workspace-nav"
      >
        <nav
          className="overflow-x-auto"
          aria-label="Navigation de la fiche prospect"
          data-testid="prospect-horizontal-tabs"
        >
          <div className="flex min-w-max px-2 sm:px-3">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const selected =
                activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={(event) => {
                    setActiveTab(tab.id);
                    setPage(1);

                    event.currentTarget.scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                      inline: "center",
                    });
                  }}
                  className={`relative inline-flex min-h-[50px] items-center justify-center gap-2 whitespace-nowrap border-b-[3px] px-3.5 text-sm font-bold transition ${
                    selected
                      ? "border-[var(--do-accent)] text-[var(--do-accent)]"
                      : "border-transparent text-[var(--do-muted)] hover:border-[var(--do-border-strong)] hover:text-[var(--do-accent)]"
                  }`}
                  aria-current={
                    selected
                      ? "page"
                      : undefined
                  }
                  data-testid={`prospect-tab-${tab.id}`}
                >
                  <Icon
                    size={18}
                    weight={
                      selected
                        ? "fill"
                        : "duotone"
                    }
                  />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </nav>
      </section>

      {/* Mobile : réutilise l'accordéon UI Acta existant. */}
      <section
        className="au-card overflow-hidden md:hidden"
        data-testid="prospect-mobile-accordion"
      >
        <Accordion
          type="single"
          value={activeTab}
          onValueChange={(value) => {
            if (!value) {
              return;
            }

            setActiveTab(value);
            setPage(1);
          }}
          collapsible
          aria-label="Navigation mobile de la fiche prospect"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const selected =
              activeTab === tab.id;

            return (
              <AccordionItem
                key={tab.id}
                value={tab.id}
                className="border-[var(--do-border)] last:border-b-0"
                data-testid={`prospect-mobile-section-${tab.id}`}
              >
                <AccordionTrigger
                  className={`min-h-[54px] gap-3 px-4 py-3 text-left font-heading text-sm font-black hover:no-underline ${
                    selected
                      ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                      : "bg-[var(--do-bg-solid)] text-[var(--do-ink)]"
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <Icon
                      size={19}
                      weight={
                        selected
                          ? "fill"
                          : "duotone"
                      }
                      className="shrink-0"
                    />
                    <span className="truncate">
                      {tab.label}
                    </span>
                  </span>
                </AccordionTrigger>

                <AccordionContent className="border-t border-[var(--do-border)] p-3">
                  <div
                    className="min-w-0"
                    data-testid={`prospect-mobile-content-${tab.id}`}
                  >
                    {selected
                      ? renderContent()
                      : null}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </section>

      <main
        className="hidden min-w-0 md:block"
        data-testid="prospect-workspace-content"
      >
        {renderContent()}
      </main>

      <ContactDeleteConfirmModal
        open={contactDeleteModalOpen}
        contactLabel={
          [
            p.firstName,
            p.lastName,
          ]
            .filter(Boolean)
            .join(" ")
          || p.company
          || "ce contact"
        }
        dependencies={contactDeleteImpact}
        deleting={contactDeleting}
        onClose={() => {
          if (!contactDeleting) {
            setContactDeleteModalOpen(false);
          }
        }}
        onConfirm={deleteContactRecord}
      />

      <QuotePdfPreviewModal
        open={Boolean(saleQuotePreview?.quoteId)}
        quoteId={saleQuotePreview?.quoteId || ""}
        title={saleQuotePreview?.title || "Devis"}
        onClose={() => setSaleQuotePreview(null)}
      />

      {editOpen && (
        <Modal
          title="Modifier les informations"
          onClose={() => setEditOpen(false)}
          testid="edit-prospect-modal"
          closeOnBackdrop={false}
        >
          <ContactFormFields
            value={editForm}
            onChange={setEditForm}
            onAddressBlur={resolveEditAddress}
          />

          <div className="flex justify-end gap-2 pt-5">
            <button onClick={() => setEditOpen(false)} className="px-4 py-2 text-sm border border-[var(--do-border)] rounded">Annuler</button>
            <button
              onClick={saveEdit}
              disabled={saving}
              className="px-4 py-2 text-sm bg-[var(--do-accent-strong)] text-[var(--do-on-accent)] rounded disabled:bg-[var(--do-bg-2)]"
              data-testid="save-prospect-contact"
            >
              Enregistrer
            </button>
          </div>
        </Modal>
      )}

      {infoModalOpen && (
        <Modal
          title={
            activeCallOutcomeUi?.title ||
            (
              newInfoType === "note"
                ? "Ajouter une note"
                : "Ajouter une activité"
            )
          }
          onClose={
            resetComplementaryInfo
          }
          testid="complementary-info-modal"
        >
          {activeCallOutcomeUi && (
            <div
              className="mb-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4"
              data-testid="call-outcome-context"
            >
              <div className="text-sm font-black text-[var(--do-ink)]">
                {activeCallOutcomeUi.title}
              </div>

              <p className="mt-1 text-sm leading-5 text-[var(--do-muted)]">
                {activeCallOutcomeUi.description}
              </p>

              {callOutcomeContext ===
                "answered" && (
                <div
                  className="mt-4 grid grid-cols-2 gap-2"
                  data-testid="call-answered-quick-actions"
                >
                  <button
                    type="button"
                    onClick={() => {
                      resetComplementaryInfo();
                      setActiveTab("sales");
                      setPage(1);
                      window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }}
                    className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-ink)]"
                  >
                    Voir la fiche
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      resetComplementaryInfo();
                      setActiveTab("sales");
                      setPage(1);
                    }}
                    className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-accent)]"
                  >
                    Créer une opportunité
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      resetComplementaryInfo();
                      setDocPicker(true);
                    }}
                    className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-ink)]"
                  >
                    Partager
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      resetComplementaryInfo();
                      openStandaloneMeeting();
                    }}
                    className="min-h-[44px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-accent)]"
                  >
                    Rendez-vous
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm font-semibold text-[var(--do-ink)]">
              Type d'activité

              <select
                value={newInfoType}
                onChange={(event) =>
                  setNewInfoType(
                    event.target.value
                  )
                }
                className="mt-2 w-full rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm outline-none focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
                data-testid="complementary-info-type"
              >
                <option value="call">
                  Appel
                </option>

                <option value="email">
                  Email
                </option>

                <option value="meeting">
                  Rendez-vous
                </option>

                <option value="followup">
                  Relance
                </option>

                <option value="note">
                  Note libre
                </option>
              </select>
            </label>

            {newInfoType !== "note" &&
              !callOutcomeContext && (
              <label className="block text-sm font-semibold text-[var(--do-ink)]">
                Résultat

                <select
                  value={newInfoOutcome}
                  onChange={(event) =>
                    setNewInfoOutcome(
                      event.target.value
                    )
                  }
                  className="mt-2 w-full rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm outline-none focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
                  data-testid="commercial-activity-outcome"
                >
                  <option value="">
                    Non renseigné
                  </option>

                  {Object.entries(
                    OUTCOME_LABELS
                  ).map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>
            )}
          </div>

          <TextArea
            label={
              activeCallOutcomeUi
                ?.contentLabel ||
              (
                newInfoType === "note"
                  ? "Note"
                  : "Compte rendu"
              )
            }
            value={newInfo}
            onChange={setNewInfo}
            placeholder={
              activeCallOutcomeUi
                ?.placeholder ||
              (
                newInfoType === "note"
                  ? "Écrivez l'information à retenir sur ce prospect..."
                  : "Résumez l'échange avec le prospect..."
              )
            }
            max={2000}
            testid="complementary-info-input"
          />

          {newInfoType ===
            "meeting" && (
            <div
              className="mt-2 flex flex-wrap items-center gap-2"
              data-testid="meeting-report-dictation"
            >
              <button
                type="button"
                disabled={
                  !meetingDictation
                    .supported
                }
                onClick={() => {
                  if (
                    meetingDictation
                      .listening
                  ) {
                    meetingDictation
                      .stop();
                  } else {
                    meetingDictation
                      .start();
                  }
                }}
                className="min-h-[42px] rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-sm font-black text-[var(--do-ink)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {meetingDictation
                  .listening
                  ? "◉ Arrêter la dictée"
                  : "🎙 Dicter le compte rendu"}
              </button>

              {!meetingDictation
                .supported && (
                <span className="text-xs text-[var(--do-muted)]">
                  Dictée vocale non disponible dans ce navigateur.
                </span>
              )}
            </div>
          )}

          <div
            className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4"
            data-testid="commercial-next-action-section"
          >
            <div className="text-sm font-black text-[var(--do-ink)]">
              Prochaine action
            </div>

            <p className="mt-1 text-xs text-[var(--do-muted)]">
              Facultatif. La description et la date doivent être renseignées ensemble.
            </p>

            {callOutcomeContext &&
              (
                nextAction ||
                nextActionAt
              ) && (
                <button
                  type="button"
                  onClick={() => {
                    setNextAction("");
                    setNextActionAt("");
                  }}
                  className="mt-3 text-xs font-black text-[var(--do-accent)] underline underline-offset-4"
                  data-testid="call-outcome-clear-followup"
                >
                  Ne pas planifier de prochaine action
                </button>
              )}

            <input
              type="text"
              value={nextAction}
              onChange={(event) =>
                setNextAction(
                  event.target.value
                )
              }
              placeholder="Ex. rappeler pour confirmer la démonstration"
              maxLength={500}
              className="mt-3 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
              data-testid="commercial-next-action"
            />

            <div className="mt-3 grid sm:grid-cols-2 gap-3">
              <label className="block text-sm font-semibold text-[var(--do-ink)]">
                Type d'action

                <select
                  value={nextActionType}
                  onChange={(event) =>
                    setNextActionType(
                      event.target.value
                    )
                  }
                  className="mt-2 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
                  data-testid="commercial-next-action-type"
                >
                  {Object.entries(
                    NEXT_ACTION_LABELS
                  ).map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label className="block text-sm font-semibold text-[var(--do-ink)]">
                Date et heure

                <input
                  type="datetime-local"
                  value={nextActionAt}
                  onChange={(event) =>
                    setNextActionAt(
                      event.target.value
                    )
                  }
                  className="prospect-mobile-datetime mt-2 w-full min-w-0 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-base outline-none focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)] sm:text-sm"
                  data-testid="commercial-next-action-at"
                />
              </label>
            </div>

            {nextActionType === "followup" && (
              <label className="mt-3 block text-sm font-semibold text-[var(--do-ink)]">
                Canal de relance

                <select
                  value={nextActionChannel}
                  onChange={(event) =>
                    setNextActionChannel(
                      event.target.value
                    )
                  }
                  className="mt-2 w-full rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-sm outline-none focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
                  data-testid="commercial-next-action-channel"
                >
                  {Object.entries(
                    FOLLOWUP_CHANNEL_LABELS
                  ).map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </label>
            )}
          </div>

          <div className="flex flex-col-reverse gap-2 pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={
                resetComplementaryInfo
              }
              className="min-h-[44px] rounded-xl border border-[var(--do-border)] px-4 py-2 text-sm font-bold"
            >
              Fermer
            </button>

            <button
              type="button"
              onClick={
                saveComplementaryInfo
              }
              disabled={
                saving ||
                (
                  newInfoType === "note" &&
                  !newInfo.trim()
                )
              }
              className="min-h-[44px] rounded-xl bg-[var(--do-accent-strong)] px-4 py-2 text-sm font-black text-[var(--do-on-accent)] disabled:bg-[var(--do-bg-2)]"
              data-testid="commercial-activity-save"
            >
              {saving
                ? "Enregistrement…"
                : (
                  callOutcomeContext
                    ? "Enregistrer les informations"
                    : "Enregistrer"
                )}
            </button>
          </div>
        </Modal>
      )}

      {infoToRead && (
        <Modal title="Commentaire lié au prospect" onClose={() => setInfoToRead(null)} testid="read-complementary-info-modal">
          <div className="text-sm text-[var(--do-ink)] whitespace-pre-wrap leading-7">{infoToRead.content}</div>
          <div className="text-xs text-[var(--do-muted)] mt-4">
            {fmtDateTime(infoToRead.createdAt || infoToRead.activityDate)} · {infoToRead.authorName || "—"}
          </div>
          <div className="flex justify-end pt-5">
            <button onClick={() => setInfoToRead(null)} className="px-4 py-2 text-sm bg-[var(--do-accent-strong)] text-[var(--do-on-accent)] rounded">Fermer</button>
          </div>
        </Modal>
      )}

      {docPicker && (
        <div className="acta-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" onClick={() => setDocPicker(false)} data-testid="doc-picker-modal">
          <div className="acta-modal-surface w-full max-w-md rounded-3xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Choisir les documents à partager">
            <div className="px-5 py-3.5 border-b border-[var(--do-border)] font-heading font-bold">Choisir les documents à partager</div>
            <div className="p-3 max-h-[60vh] overflow-y-auto">
              {documents.length === 0 ? (
                <div className="text-center py-8 text-sm text-[var(--do-muted)]">Aucun document disponible.</div>
              ) : (
                <ul className="space-y-1">
                  {documents.map((d) => (
                    <li key={d.id}>
                      <label className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-[var(--do-accent-soft)] transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedShareDocumentIds.includes(d.id)}
                          onChange={(event) => {
                            setSelectedShareDocumentIds((current) =>
                              event.target.checked
                                ? [...current, d.id]
                                : current.filter((id) => id !== d.id)
                            );
                          }}
                          data-testid={`pick-doc-${d.id}`}
                        />
                        <FileText size={14} weight="duotone" className="text-[var(--do-accent)]" />
                        <span className="text-sm flex-1 truncate">{d.name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-[var(--do-border)]">
              <button type="button" onClick={() => { setSelectedShareDocumentIds([]); setDocPicker(false); }} className="px-4 py-2 rounded-md text-sm">Annuler</button>
              <button
                type="button"
                disabled={selectedShareDocumentIds.length === 0}
                onClick={() => {
                  const selectedDocuments = documents.filter((document) =>
                    selectedShareDocumentIds.includes(document.id)
                  );
                  if (!selectedDocuments.length) return;
                  setShareItem(
                    selectedDocuments.length === 1
                      ? { type: "document", id: selectedDocuments[0].id, name: selectedDocuments[0].name }
                      : {
                          type: "selection",
                          name: `${selectedDocuments.length} documents`,
                          items: selectedDocuments.map((document) => ({
                            type: "document",
                            id: document.id,
                            name: document.name,
                          })),
                        }
                  );
                  setSelectedShareDocumentIds([]);
                  setDocPicker(false);
                }}
                className="px-4 py-2 rounded-md text-sm bg-[var(--do-accent-strong)] text-[var(--do-on-accent)] disabled:opacity-50"
              >
                Partager les documents sélectionnés ({selectedShareDocumentIds.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {shareItem && (
        <ShareModal
          item={shareItem}
          defaultProspect={p}
          lockRecipient
          onClose={() => {
            setShareItem(null);
            load();
          }}
        />
      )}

      {documentPreview && (
        <DocumentPreviewModal
          doc={documentPreview}
          onClose={() => setDocumentPreview(null)}
        />
      )}
    </div>
  );
}





function QuotesPanel({
  prospect,
  quotes = [],
  loading = false,
  createForm = null,
  saving = false,
  isAdmin = false,
  onSubmit,
  onApprove,
  onReject,
  onDownloadPdf,
  onSend,
  onAccept,
  onRefuse,
}) {
  const [quotePage, setQuotePage] = useState(1);

  const statusLabels = {
    draft: "Brouillon",
    pending_validation: "En attente admin",
    approved: "Validé à envoyer",
    rejected: "Rejeté",
    sent: "Envoyé",
    accepted: "Accepté",
    refused: "Refusé",
    purchase_order_created: "Accepté",
  };

  const statusClass = (status) => {
    if (["approved", "accepted", "purchase_order_created"].includes(status)) {
      return "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]";
    }

    if (status === "pending_validation") {
      return "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]";
    }

    if (["rejected", "refused"].includes(status)) {
      return "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]";
    }

    if (status === "sent") {
      return "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]";
    }

    return "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-muted)]";
  };

  const quoteTotalPages = Math.max(
    1,
    Math.ceil((quotes || []).length / QUOTE_DETAIL_PAGE_SIZE)
  );

  const safeQuotePage = Math.min(
    Math.max(quotePage, 1),
    quoteTotalPages
  );

  const quoteStartIndex =
    (safeQuotePage - 1) * QUOTE_DETAIL_PAGE_SIZE;

  const paginatedQuotes = (quotes || []).slice(
    quoteStartIndex,
    quoteStartIndex + QUOTE_DETAIL_PAGE_SIZE
  );

  return (
    <section
      className="space-y-5"
      data-testid="prospect-quotes-panel"
    >
      <div className="au-card p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="label-eyebrow">
              Devis
            </div>

            <h2 className="mt-2 font-heading text-2xl font-black tracking-tight text-[var(--do-ink)]">
              Devis du prospect / client
            </h2>

            <p className="mt-2 text-sm text-[var(--do-muted)]">
              Workflow : brouillon → validation admin → envoi client → accepté ou refusé.
            </p>
          </div>
        </div>

        {createForm && (
          <div className="mt-5">
            {createForm}
          </div>
        )}
      </div>

      <div className="au-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-xl font-black text-[var(--do-ink)]">
              Liste des devis
            </h3>

            <p className="mt-1 text-sm text-[var(--do-muted)]">
              {quotes.length} devis associé(s) à {prospect?.company || prospect?.email || "ce contact"}.
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {loading && (
            <div className="text-sm text-[var(--do-muted)]">
              Chargement des devis…
            </div>
          )}

          {!loading && quotes.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] p-6 text-sm text-[var(--do-muted)]">
              Aucun devis associé pour le moment.
            </div>
          )}

          {paginatedQuotes.map((quote) => (
            <article
              key={quote.id}
              className="rounded-3xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4"
              data-testid="prospect-quote-item"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--do-muted)]">
                    {quote.quoteNumber || quote.id}
                  </div>

                  <h4 className="mt-1 font-heading text-lg font-black text-[var(--do-ink)]">
                    {quote.title || "Devis commercial"}
                  </h4>

                  <p className="mt-1 text-sm text-[var(--do-muted)]">
                    {quote.machineName || "Machine non renseignée"}
                  </p>
                </div>

                <div className="text-right">
                  <div className="font-heading text-xl font-black text-[var(--do-ink)]">
                    {fmtCurrency(quote.amount)}
                  </div>

                  <span
                    className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-black ${statusClass(quote.status)}`}
                    data-testid="prospect-quote-status-badge"
                  >
                    {statusLabels[quote.status] || quote.status}
                  </span>
                </div>
              </div>

              {quote.status === "accepted" && (
                <div
                  className="mt-3 flex flex-wrap gap-2"
                  data-testid="prospect-quote-financing-state"
                >
                  {(
                    quote.financingStatus ||
                    "pending"
                  ) === "pending" && (
                    <>
                      <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                        CA provisoire
                      </span>
                      <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                        Financement en attente
                      </span>
                    </>
                  )}

                  {quote.financingStatus === "approved" && (
                    <>
                      <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                        CA confirmé
                      </span>
                      <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                        Financement accepté
                      </span>
                      {quote.gmaoStatus === "ready_for_transfer" && (
                        <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                          Installation GMAO à préparer
                        </span>
                      )}
                      {quote.gmaoStatus === "transferred" && (
                        <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                          Transféré dans la GMAO
                        </span>
                      )}
                    </>
                  )}

                  {quote.financingStatus === "refused" && (
                    <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                      Financement refusé · CA non confirmé
                    </span>
                  )}

                  {quote.financingStatus === "cancelled" && (
                    <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                      Financement annulé · CA non confirmé
                    </span>
                  )}
                </div>
              )}

              {quote.rejectionReason && (
                <div className="mt-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-xs text-[var(--do-accent)]">
                  Motif de rejet : {quote.rejectionReason}
                </div>
              )}

              {quote.refusalReason && (
                <div className="mt-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-xs text-[var(--do-accent)]">
                  Refus client : {quote.refusalReason}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {QUOTE_PDF_ALLOWED_STATUSES.has(quote.status) && (
                  <button
                    type="button"
                    onClick={() => onDownloadPdf?.(quote)}
                    className="inline-flex items-center gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-2 text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-solid)]"
                    data-testid="quote-download-pdf-action"
                  >
                    PDF
                  </button>
                )}

                {(quote.status === "draft" || quote.status === "rejected") && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => onSubmit?.(quote)}
                    className="inline-flex items-center gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)] disabled:opacity-50"
                    data-testid="quote-submit-action"
                  >
                    Soumettre admin
                  </button>
                )}

                {isAdmin && quote.status === "pending_validation" && (
                  <>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => onApprove?.(quote)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)] disabled:opacity-50"
                      data-testid="quote-approve-action"
                    >
                      Valider
                    </button>

                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => onReject?.(quote)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)] disabled:opacity-50"
                      data-testid="quote-reject-action"
                    >
                      Rejeter
                    </button>
                  </>
                )}

                {quote.status === "approved" && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => onSend?.(quote)}
                    className="inline-flex items-center gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)] disabled:opacity-50"
                    data-testid="quote-send-action"
                  >
                    Envoyer au client
                  </button>
                )}

                {quote.status === "sent" && (
                  <>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => onAccept?.(quote)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)] disabled:opacity-50"
                      data-testid="quote-accept-action"
                    >
                      Accepté
                    </button>

                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => onRefuse?.(quote)}
                      className="inline-flex items-center gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)] disabled:opacity-50"
                      data-testid="quote-refuse-action"
                    >
                      Refusé
                    </button>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>

        {!loading && quotes.length > QUOTE_DETAIL_PAGE_SIZE && (
          <div
            className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3"
            data-testid="prospect-quote-pagination"
          >
            <div className="text-xs font-semibold text-[var(--do-muted)]">
              {quoteStartIndex + 1}-
              {Math.min(quoteStartIndex + QUOTE_DETAIL_PAGE_SIZE, quotes.length)}
              {" "}sur {quotes.length} devis
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setQuotePage((current) => Math.max(1, current - 1))}
                disabled={safeQuotePage <= 1}
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-muted)] disabled:opacity-50"
                data-testid="prospect-quote-page-prev"
              >
                Précédent
              </button>

              <span className="text-xs font-black text-[var(--do-muted)]">
                Page {safeQuotePage} / {quoteTotalPages}
              </span>

              <button
                type="button"
                onClick={() => setQuotePage((current) => Math.min(quoteTotalPages, current + 1))}
                disabled={safeQuotePage >= quoteTotalPages}
                className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-muted)] disabled:opacity-50"
                data-testid="prospect-quote-page-next"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function CommercialStat({
  label,
  value,
  testId,
}) {
  return (
    <div
      className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4"
      data-testid={testId}
    >
      <div className="label-eyebrow">
        {label}
      </div>

      <div className="mt-2 text-lg font-black text-[var(--do-ink)]">
        {value}
      </div>
    </div>
  );
}


/**
 * Vue d'ensemble du prospect.
 */
function ProspectProfileHeader({
  prospect,
  canAssignCommercial = false,
  commercialUsers = [],
  commercialUsersLoading = false,
  assigningCommercial = false,
  onAssignCommercial,
  collaborators = [],
  collaboratorCandidates = [],
  collaboratorIdToAdd = "",
  collaboratorsLoading = false,
  collaboratorsSaving = false,
  onCollaboratorSelect,
  onCollaboratorAdd,
  onCollaboratorRemove,
  onEdit,
  onPurchaseIntentChange,
  purchaseIntentSaving = false,
  onDelete,
  deleting = false,
  onShare,
  onMeeting,
  onEmail,
  onCreateQuote,
  onActivityRefresh,
  onCallFollowup,
  onCallNewAction,
  onCallNote,
  requestedCallOutcomeId = "",
  requestedCallActivity = null,
  requestedAgentCall = "",
  onConsumeAgentCall,
}) {
  // ACTA_AI_EVENT_TRACKING_V1
  // ACTA_PROSPECT_CALL_CHANNELS_V1
  const [
    callChannelSheetOpen,
    setCallChannelSheetOpen,
  ] = useState(false);

  const [
    callSaving,
    setCallSaving,
  ] = useState(false);

  const agentCallHandledRef =
    useRef("");

  const callSession =
    usePendingCallSession({
      contactId: prospect.id,
      fallbackActivity: requestedCallActivity,
      fallbackContact: prospect,
      forceOpen: Boolean(requestedCallOutcomeId),
    });

  const startProspectCall = async (
    channel
  ) => {
    if (
      !prospect.phone ||
      callSaving
    ) {
      return;
    }

    setCallSaving(true);

    try {
      const activity =
        await prospectsApi.startCall(
          prospect.id,
          channel,
          "prospect_profile"
        );

      callSession.remember(
        activity,
        prospect
      );

      setCallChannelSheetOpen(false);
      callSession.closeReturn();

      const target =
        channel === "whatsapp"
          ? whatsappUrl(
              prospect.phone,
              ""
            )
          : `tel:${prospect.phone}`;

      // ACTA_PROSPECT_WHATSAPP_IOS_PWA_V1
      // L’ouverture d’un nouvel onglet après l’appel API
      // est bloquée comme une popup par Safari iOS.
      window.location.href =
        target;

      onActivityRefresh?.();
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) ||
          "Impossible de démarrer l’appel"
      );
    } finally {
      setCallSaving(false);
    }
  };

  useEffect(() => {
    if (
      requestedAgentCall !==
        "whatsapp"
      || !prospect?.id
      || !prospect?.phone
    ) {
      return;
    }

    const handledKey =
      `${prospect.id}:whatsapp`;

    if (
      agentCallHandledRef.current ===
      handledKey
    ) {
      return;
    }

    agentCallHandledRef.current =
      handledKey;

    // Le paramètre est consommé avant
    // l'ouverture de WhatsApp afin qu'un
    // refresh ne relance jamais l'action.
    onConsumeAgentCall?.();

    // Réutilisation stricte du workflow
    // métier déjà présent dans Acta :
    // activité d'appel + pending call
    // + ouverture WhatsApp.
    startProspectCall(
      "whatsapp"
    );

    // startProspectCall et onConsumeAgentCall
    // sont volontairement issus de ce workflow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    requestedAgentCall,
    prospect?.id,
    prospect?.phone,
  ]);

  const saveCallOutcome = async (
    resultCode
  ) => {
    if (
      !callSession.pending?.activity?.id ||
      callSaving
    ) {
      return false;
    }

    setCallSaving(true);

    try {
      await prospectsApi.recordCallOutcome(
        callSession.pending.activity.id,
        resultCode
      );

      callSession.markResolved();
      toast.success("Résultat de l’appel enregistré");
      try {
        await onActivityRefresh?.();
      } catch {
        // L'appel est déjà enregistré : un échec de rechargement
        // ne doit pas provoquer une deuxième catégorisation.
        toast.error("Actualisation des activités impossible. Rechargez la fiche.");
      }
      return true;
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
          "Impossible d’enregistrer le résultat"
      );
      return false;
    } finally {
      setCallSaving(false);
    }
  };

  const finishCallAndRun = (action) => {
    callSession.finish();
    action?.();
  };

  // ACTA_PROSPECT_COMMERCIAL_HUMAN_LABEL_RC1_V1
  const assignedCommercial =
    commercialUsers.find((item) => {
      const commercialId =
        item.id ||
        item.userId ||
        item.user_id ||
        item._id;

      return (
        commercialId &&
        String(commercialId) ===
          String(
            prospect.assignedUserId ||
              ""
          )
      );
    });

  const assignedCommercialLabel =
    prospect.assignedUserName ||
    prospect.assigned_user_name ||
    prospect.ownerName ||
    prospect.owner_name ||
    assignedCommercial?.name ||
    assignedCommercial?.fullName ||
    assignedCommercial?.full_name ||
    assignedCommercial?.email ||
    (
      prospect.assignedUserId
        ? "Commercial assigné"
        : "Non assigné"
    );

  const [
    pendingCommercialId,
    setPendingCommercialId,
  ] = useState(
    prospect.assignedUserId || ""
  );

  useEffect(() => {
    setPendingCommercialId(
      prospect.assignedUserId || ""
    );
  }, [
    prospect.id,
    prospect.assignedUserId,
  ]);

  const currentCommercialId =
    prospect.assignedUserId || "";

  const commercialHasChanged =
    pendingCommercialId !==
    currentCommercialId;

  const pendingCommercial =
    commercialUsers.find((item) => {
      const commercialId =
        item.id ||
        item.userId ||
        item.user_id ||
        item._id;

      return (
        commercialId &&
        String(commercialId) ===
          String(
            pendingCommercialId || ""
          )
      );
    });

  const pendingCommercialLabel =
    pendingCommercial?.name ||
    pendingCommercial?.fullName ||
    pendingCommercial?.full_name ||
    pendingCommercial?.email ||
    (
      pendingCommercialId
        ? "Commercial sélectionné"
        : "Non assigné"
    );

  const confirmCommercialAssignment = () => {
    if (
      !commercialHasChanged ||
      assigningCommercial
    ) {
      return;
    }

    const confirmed = window.confirm(
      [
        "Confirmer la réaffectation commerciale ?",
        "",
        `Ancien commercial : ${assignedCommercialLabel}`,
        `Nouveau commercial : ${pendingCommercialLabel}`,
        "",
        "Cette modification sera enregistrée dans l’historique.",
      ].join("\n")
    );

    if (!confirmed) {
      return;
    }

    onAssignCommercial?.(
      pendingCommercialId
    );
  };

  const fullName =
    [
      prospect.firstName,
      prospect.lastName,
    ]
      .filter(Boolean)
      .join(" ") ||
    prospect.company ||
    "Contact sans nom";

  const initials =
    [
      prospect.firstName?.[0],
      prospect.lastName?.[0],
    ]
      .filter(Boolean)
      .join("")
      .toUpperCase() ||
    prospect.company?.[0]?.toUpperCase() ||
    "?";

  const lifecycleLabel =
    prospect.lifecycleStage === "client"
      ? "Client"
      : "Prospect";

  const hasNextAction =
    Boolean(prospect.nextAction) &&
    prospect.nextActionStatus !==
      "completed";

  const nextActionIsLate =
    hasNextAction &&
    Boolean(prospect.nextActionAt) &&
    new Date(
      prospect.nextActionAt
    ).getTime() < Date.now();

  const actionButtonClass =
    "inline-flex min-h-[42px] min-w-0 items-center justify-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-xs font-bold text-[var(--do-ink)] transition hover:border-[var(--do-border)] hover:bg-[var(--do-accent-soft)] hover:text-[var(--do-accent)] disabled:cursor-not-allowed disabled:opacity-40 sm:gap-2 sm:px-4 sm:text-sm";

  return (
    <section
      className="au-card min-w-0 overflow-hidden"
      data-testid="prospect-profile-header"
    >
      <div className="min-w-0 p-3.5 sm:p-5">
        <div className={`flex flex-col gap-4 xl:grid xl:items-start ${
          canAssignCommercial
            ? "xl:grid-cols-[minmax(320px,1fr)_minmax(400px,640px)]"
            : "xl:grid-cols-[minmax(320px,1fr)_minmax(260px,420px)]"
        }`}>
          <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center sm:gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[var(--do-accent-2)] to-[var(--do-accent)] text-base font-black text-[var(--do-on-accent)] shadow-md sm:h-16 sm:w-16 sm:rounded-[20px] sm:text-xl sm:shadow-lg">
              {initials}
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="min-w-0 break-words font-heading text-xl font-black tracking-tight text-[var(--do-ink)] sm:text-2xl">
                  {fullName}
                </h2>

                <span
                  className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${
                    prospect.lifecycleStage ===
                    "client"
                      ? "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                      : "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                  }`}
                  data-testid="computed-status-badge"
                >
                  {lifecycleLabel}
                </span>
              </div>

              {/* ACTA_CONTACT_PROFILE_FULL_FORM_VIEW_V1 */}
              <dl className="mt-3 grid min-w-0 gap-x-5 gap-y-2 text-sm sm:grid-cols-2">
                <div
                  className="min-w-0"
                  data-testid="contact-company-detail"
                >
                  <dt className="text-xs font-bold text-[var(--do-muted)]">
                    Société
                  </dt>
                  <dd className="mt-0.5 break-words font-bold text-[var(--do-ink)]">
                    {prospect.company || ""}
                  </dd>
                </div>

                <div
                  className="min-w-0"
                  data-testid="contact-position-detail"
                >
                  <dt className="text-xs font-bold text-[var(--do-muted)]">
                    Fonction
                  </dt>
                  <dd className="mt-0.5 break-words text-[var(--do-ink)]">
                    {prospect.position || ""}
                  </dd>
                </div>

                <div
                  className="min-w-0"
                  data-testid="contact-email-detail"
                >
                  <dt className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--do-muted)]">
                    <EnvelopeSimple
                      size={15}
                      className="text-[var(--do-accent)]"
                    />
                    E-mail
                  </dt>
                  <dd className="mt-0.5 break-all text-[var(--do-ink)]">
                    {prospect.email || ""}
                  </dd>
                </div>

                <div
                  className="min-w-0"
                  data-testid="contact-phone-detail"
                >
                  <dt className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--do-muted)]">
                    <Phone
                      size={15}
                      className="text-[var(--do-accent)]"
                    />
                    Téléphone
                  </dt>
                  <dd className="mt-0.5 break-words text-[var(--do-ink)]">
                    {prospect.phone || ""}
                  </dd>
                </div>

                <div
                  className="min-w-0 sm:col-span-2"
                  data-testid="contact-address-detail"
                >
                  <dt className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--do-muted)]">
                    <MapPin
                      size={15}
                      className="text-[var(--do-accent)]"
                    />
                    Adresse
                  </dt>
                  <dd className="mt-0.5 break-words text-[var(--do-ink)]">
                    {prospect.address || ""}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div
            className="grid min-w-0 w-full gap-2 sm:grid-cols-2"
            data-testid="prospect-header-summary"
          >
            {canAssignCommercial && (
              <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)]/60 p-3">
                <div className="label-eyebrow">
                  Commercial responsable
                </div>

                <div
                  className="mt-2 space-y-2"
                  data-testid="commercial-assignment-control"
                >
                  <select
                    value={
                      pendingCommercialId
                    }
                    disabled={
                      commercialUsersLoading ||
                      assigningCommercial
                    }
                    onChange={(event) =>
                      setPendingCommercialId(
                        event.target.value
                      )
                    }
                    data-testid="assigned-commercial-select"
                    className="w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm font-bold text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)]"
                  >
                    <option value="">
                      Non assigné
                    </option>

                    {commercialUsers.map(
                      (commercial) => (
                        <option
                          key={commercial.id}
                          value={commercial.id}
                        >
                          {commercial.name ||
                            commercial.fullName ||
                            commercial.full_name ||
                            commercial.email ||
                            "Commercial"}
                        </option>
                      )
                    )}
                  </select>

                  {commercialHasChanged && (
                    <div
                      className="rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-2.5"
                      data-testid="commercial-assignment-pending"
                    >
                      <p className="text-xs font-bold leading-5 text-[var(--do-ink)]">
                        Changement en attente :
                        {" "}
                        {assignedCommercialLabel}
                        {" → "}
                        {pendingCommercialLabel}
                      </p>

                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={
                            assigningCommercial
                          }
                          onClick={
                            confirmCommercialAssignment
                          }
                          className="inline-flex min-h-[36px] items-center justify-center rounded-lg bg-[var(--do-accent)] px-3 py-2 text-xs font-black text-[var(--do-on-accent)] transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                          data-testid="confirm-commercial-assignment"
                        >
                          {assigningCommercial
                            ? "Enregistrement…"
                            : "Valider le changement"}
                        </button>

                        <button
                          type="button"
                          disabled={
                            assigningCommercial
                          }
                          onClick={() =>
                            setPendingCommercialId(
                              currentCommercialId
                            )
                          }
                          className="inline-flex min-h-[36px] items-center justify-center rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-ink)] transition hover:bg-[var(--do-bg-solid)] disabled:opacity-50"
                          data-testid="cancel-commercial-assignment"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  )}

                  {!commercialHasChanged && (
                    <p className="text-[11px] font-semibold leading-4 text-[var(--do-muted)]">
                      Sélectionnez un autre commercial,
                      puis validez explicitement le changement.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)]/60 p-3"
              data-testid="contact-collaborators-control"
            >
              <div className="label-eyebrow">
                Collaborateurs
              </div>

              <div className="mt-2 space-y-2">
                <div className="flex gap-2">
                  <select
                    value={collaboratorIdToAdd}
                    disabled={
                      collaboratorsLoading ||
                      collaboratorsSaving ||
                      collaboratorCandidates.length === 0
                    }
                    onChange={(event) =>
                      onCollaboratorSelect?.(
                        event.target.value
                      )
                    }
                    className="min-w-0 flex-1 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm font-bold text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)]"
                    data-testid="contact-collaborator-select"
                  >
                    <option value="">
                      {collaboratorCandidates.length
                        ? "Ajouter un collaborateur"
                        : "Aucun utilisateur disponible"}
                    </option>

                    {collaboratorCandidates.map(
                      (candidate) => {
                        const label = [
                          candidate.firstName,
                          candidate.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <option
                            key={candidate.id}
                            value={candidate.id}
                          >
                            {label ||
                              candidate.email ||
                              "Utilisateur"}
                          </option>
                        );
                      }
                    )}
                  </select>

                  <button
                    type="button"
                    disabled={
                      !collaboratorIdToAdd ||
                      collaboratorsSaving
                    }
                    onClick={() =>
                      onCollaboratorAdd?.()
                    }
                    className="min-h-[40px] rounded-xl bg-[var(--do-accent)] px-3 text-xs font-black text-[var(--do-on-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="contact-collaborator-add"
                  >
                    Ajouter
                  </button>
                </div>

                {collaborators.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {collaborators.map(
                      (collaborator) => {
                        const label = [
                          collaborator.firstName,
                          collaborator.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <span
                            key={collaborator.id}
                            className="inline-flex items-center gap-1 rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-1 text-xs font-bold text-[var(--do-ink)]"
                          >
                            {label ||
                              collaborator.email ||
                              "Collaborateur"}

                            <button
                              type="button"
                              disabled={
                                collaboratorsSaving
                              }
                              onClick={() =>
                                onCollaboratorRemove?.(
                                  collaborator.id
                                )
                              }
                              className="rounded-full px-1 text-[var(--do-muted)] hover:text-[var(--do-ink)] disabled:opacity-50"
                              aria-label={`Retirer ${label || collaborator.email || "le collaborateur"}`}
                              data-testid={`contact-collaborator-remove-${collaborator.id}`}
                            >
                              ×
                            </button>
                          </span>
                        );
                      }
                    )}
                  </div>
                ) : (
                  <p className="text-[11px] font-semibold leading-4 text-[var(--do-muted)]">
                    Aucun collaborateur ajouté.
                  </p>
                )}
              </div>
            </div>

            <div
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3"
              data-testid="contact-purchase-intent-control"
            >
              <div className="label-eyebrow">
                Intention d’achat
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <PurchaseIntentBadge value={prospect.purchaseIntent} />

                <select
                  value={normalizePurchaseIntent(prospect.purchaseIntent)}
                  disabled={purchaseIntentSaving}
                  onChange={(event) =>
                    onPurchaseIntentChange?.(event.target.value)
                  }
                  className="min-h-[40px] flex-1 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-sm font-bold text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] disabled:opacity-50"
                  aria-label="Modifier l’intention d’achat"
                  data-testid="purchase-intent-select"
                >
                  {PURCHASE_INTENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              className={`rounded-xl border p-3 ${
                nextActionIsLate
                  ? "border-[var(--do-border)] bg-[var(--do-accent-soft)]"
                  : hasNextAction
                    ? "border-[var(--do-border)] bg-[var(--do-accent-soft)]"
                    : "border-[var(--do-border)] bg-[var(--do-bg-solid)]"
              }`}
              data-testid="prospect-next-action"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="label-eyebrow">
                  Prochaine action
                </div>

                {hasNextAction && (
                  <span
                    className={`rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wide ${
                      nextActionIsLate
                        ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                        : "bg-[var(--do-bg-solid)] text-[var(--do-accent)]"
                    }`}
                  >
                    {nextActionIsLate
                      ? "En retard"
                      : "À venir"}
                  </span>
                )}
              </div>

              {hasNextAction ? (
                <>
                  <div className="mt-2 text-sm font-black text-[var(--do-ink)]">
                    {
                      NEXT_ACTION_LABELS[
                        prospect.nextActionType
                      ] ||
                      "Action commerciale"
                    }
                    {" — "}
                    {prospect.nextAction}
                  </div>

                  <div className="mt-1 text-xs font-semibold text-[var(--do-muted)]">
                    {fmtDateTime(
                      prospect.nextActionAt
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-2 text-sm font-bold text-[var(--do-muted)]">
                  Aucune action planifiée
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-3 grid min-w-0 grid-cols-2 gap-2 border-t border-[var(--do-border)] pt-3 sm:mt-4 sm:flex sm:flex-wrap sm:pt-4">
          <button
            type="button"
            disabled={!prospect.phone}
            onClick={() =>
              setCallChannelSheetOpen(
                true
              )
            }
            className={actionButtonClass}
            data-testid="prospect-call-action"
          >
            <Phone size={17} weight="bold" />
            Appeler
          </button>

          <button
            type="button"
            disabled={!prospect.email}
            onClick={onEmail}
            className={actionButtonClass}
            data-testid="prospect-email-action"
          >
            <EnvelopeSimple
              size={17}
              weight="bold"
            />
            E-mail
          </button>

          <button
            type="button"
            onClick={onMeeting}
            className={actionButtonClass}
            data-testid="prospect-meeting-action"
          >
            <Calendar size={17} weight="bold" />
            Rendez-vous
          </button>

          <button
            type="button"
            onClick={onShare}
            className={actionButtonClass}
            data-testid="share-doc-cta"
          >
            <ShareNetwork
              size={17}
              weight="bold"
            />
            Partager
          </button>

          <button
            type="button"
            onClick={onCreateQuote}
            className={actionButtonClass}
            data-testid="prospect-create-quote-action"
          >
            <FileText size={17} weight="bold" />
            Créer un devis
          </button>

          <button
            type="button"
            onClick={onEdit}
            className={`${actionButtonClass} sm:ml-auto`}
            data-testid="prospect-edit-action"
          >
            <PencilSimple
              size={17}
              weight="bold"
            />
            Modifier
          </button>

          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className={`${actionButtonClass} text-[var(--do-accent)]`}
            data-testid="prospect-delete-action"
          >
            <Trash
              size={17}
              weight="bold"
            />
            {deleting
              ? "Suppression…"
              : "Supprimer"}
          </button>
        </div>
      </div>

      <ContactCallChannelSheet
        open={callChannelSheetOpen}
        phone={prospect.phone}
        saving={callSaving}
        onClose={() => setCallChannelSheetOpen(false)}
        onSelect={startProspectCall}
        testIdPrefix="prospect"
      />

      <CallReturnDialog
        open={callSession.returnOpen}
        contact={callSession.pending?.contact || prospect}
        saving={callSaving}
        onOutcome={saveCallOutcome}
        onFollowup={() => finishCallAndRun(onCallFollowup)}
        onNewAction={() => finishCallAndRun(onCallNewAction)}
        onNote={() => finishCallAndRun(onCallNote)}
        onFinish={callSession.finish}
        onClose={callSession.closeReturn}
        testId="prospect-call-return"
      />
    </section>
  );
}


/**
 * Synthèse des indicateurs d'engagement.
 */
function OverviewPanel({
  prospect,
  data,
  sharedDocuments,
}) {
  return (
    <section
      className="au-card p-5 sm:p-6"
      data-testid="prospect-overview-panel"
    >
      <div>
        <div className="label-eyebrow">
          Synthèse
        </div>

        <h2 className="mt-2 font-heading text-xl font-black text-[var(--do-ink)]">
          Indicateurs d’engagement
        </h2>

        <p className="mt-1 text-sm text-[var(--do-muted)]">
          Activité et consultation des contenus partagés avec ce contact.
        </p>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Partages"
          value={data?.totals?.shares || 0}
          icon={ShareNetwork}
        />

        <Stat
          label="Documents partagés"
          value={
            sharedDocuments.length ||
            data?.totals?.views ||
            0
          }
          icon={FileText}
        />

        <Stat
          label="Temps lecture"
          value={fmtSeconds(
            data?.totals?.readingSeconds ||
            0
          )}
          icon={Clock}
        />

        <Stat
          label="Dernière activité"
          value={relativeTime(
            prospect.lastActivityAt ||
            data?.events?.[0]?.createdAt
          )}
          icon={Calendar}
          small
        />
      </div>
    </section>
  );
}


/**
 * Liste des activités de tracking.
 */
function TimelinePanel({
  items,
  allItems = [],
  filter = "all",
  onFilter,
  page,
  onPage,
  onAdd,
  onShare,
  onMeeting,
  onOpenItem,
  onHideItem,
  activityStateSaving = "",
}) {
  const [showTechnical, setShowTechnical] =
    useState(false);

  const pagination = paginate(
    items,
    page
  );

  const technicalItems =
    allItems.filter(
      (item) => item.technical
    );

  const filters = [
    {
      id: "all",
      label: "Tout",
      count: allItems.filter(
        (item) =>
          !item.technical &&
          !item.hidden
      ).length,
    },
    {
      id: "todo",
      label: "À traiter",
      count: allItems.filter(
        (item) =>
          item.requiresAction &&
          !item.technical &&
          !item.hidden
      ).length,
    },
    {
      id: "commercial",
      label: "Activités commerciales",
      count: allItems.filter(
        (item) =>
          item.category === "commercial" &&
          !item.technical &&
          !item.hidden
      ).length,
    },
    {
      id: "documents",
      label: "Documents",
      count: allItems.filter(
        (item) =>
          item.category === "documents" &&
          !item.technical &&
          !item.hidden
      ).length,
    },
    {
      id: "messages",
      label: "Messages",
      count: allItems.filter(
        (item) =>
          item.category === "messages" &&
          !item.technical &&
          !item.hidden
      ).length,
    },
    {
      id: "meetings",
      label: "Rendez-vous",
      count: allItems.filter(
        (item) =>
          item.category === "meetings" &&
          !item.technical &&
          !item.hidden
      ).length,
    },
  ];

  const iconClasses = {
    commercial:
      "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
    documents:
      "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
    messages:
      "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
    meetings:
      "border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)]",
    system:
      "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-muted)]",
  };

  const renderRow = (
    item,
    technical = false
  ) => {
    const Icon =
      item.icon || Sparkle;

    const iconClass =
      iconClasses[item.category] ||
      iconClasses.system;

    return (
      <article
        key={`${item.kind}-${item.id}`}
        className={`relative flex gap-3 border-b border-[var(--do-border)] px-1 py-4 last:border-b-0 ${
          technical
            ? "opacity-75"
            : ""
        }`}
        data-testid="explicit-history-row"
        data-activity-category={
          item.category
        }
      >
        <div
          className={`relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${iconClass}`}
        >
          <Icon
            size={18}
            weight="duotone"
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
            <div className="min-w-0">
              <h3
                className={`break-words text-sm leading-6 text-[var(--do-ink)] ${
                  technical
                    ? "font-bold"
                    : "font-black"
                }`}
              >
                {item.title}
              </h3>

              {item.subtitle && (
                <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-5 text-[var(--do-muted)]">
                  {item.subtitle}
                </p>
              )}

              {item.nextAction && (
                <p
                  className="mt-2 text-sm font-semibold text-[var(--do-accent)]"
                  data-testid="timeline-next-action"
                >
                  Prochaine action :{" "}
                  {item.nextAction}
                  {item.nextActionAt
                    ? ` — ${fmtDateTime(
                        item.nextActionAt
                      )}`
                    : ""}
                </p>
              )}

              {item.outcome && (
                <p className="mt-2 text-xs font-semibold text-[var(--do-muted)]">
                  Résultat :{" "}
                  {OUTCOME_LABELS[
                    item.outcome
                  ] || item.outcome}
                </p>
              )}

              {item.author &&
                !technical && (
                  <p className="mt-2 text-xs text-[var(--do-soft)]">
                    Enregistré par{" "}
                    {item.author}
                  </p>
                )}

              {!technical &&
                item.actionType &&
                item.actionLabel && (
                  <button
                    type="button"
                    onClick={() =>
                      onOpenItem?.(item)
                    }
                    className="mt-3 inline-flex items-center justify-center rounded-lg border border-[var(--do-border-strong)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-accent)] transition hover:bg-[var(--do-bg-2)]"
                    data-testid={`history-action-${item.actionType}`}
                  >
                    {item.actionLabel}
                  </button>
                )}
            </div>

            <div className="flex shrink-0 items-center gap-2 md:justify-end">
              <time className="whitespace-nowrap text-xs font-semibold text-[var(--do-muted)]">
                {fmtDateTime(item.when)}
              </time>

              {item.requiresAction && (
                <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-[11px] font-black text-[var(--do-accent)]">
                  À traiter
                </span>
              )}

              {item.isCompleted && (
                <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-2.5 py-1 text-[11px] font-black text-[var(--do-accent)]">
                  Terminé
                </span>
              )}

              {technical && (
                <span className="rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-2.5 py-1 text-[11px] font-black text-[var(--do-muted)]">
                  Info système
                </span>
              )}

              {!technical && (
                <button
                  type="button"
                  disabled={
                    activityStateSaving ===
                    `${item.kind}:${item.id}`
                  }
                  onClick={() =>
                    onHideItem?.(item)
                  }
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-muted)] transition hover:border-[var(--do-border)] hover:bg-[var(--do-accent-soft)] hover:text-[var(--do-accent)] disabled:cursor-wait disabled:opacity-50"
                  title="Masquer de mon historique"
                  aria-label="Masquer de mon historique"
                  data-testid="hide-history-activity"
                >
                  <Trash
                    size={15}
                    weight="bold"
                  />
                </button>
              )}
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <section
      className="au-card overflow-hidden"
      data-testid="prospect-activity-panel"
    >
      <div className="border-b border-[var(--do-border)] px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="font-heading text-xl font-black text-[var(--do-ink)]">
              Historique des activités
            </h2>

            <p className="mt-1 text-sm text-[var(--do-muted)]">
              Tout ce qui s’est passé avec ce contact, du plus récent au plus ancien.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-2 rounded-xl bg-[var(--do-accent)] px-3.5 py-2.5 text-sm font-black text-[var(--do-on-accent)]"
              data-testid="add-commercial-activity"
            >
              <Plus size={16} />
              Ajouter une activité
            </button>

            <button
              type="button"
              onClick={onShare}
              className="inline-flex items-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3.5 py-2.5 text-sm font-bold text-[var(--do-ink)]"
            >
              <ShareNetwork size={16} />
              Partager
            </button>
          </div>
        </div>

        <div
          className="mt-4 flex gap-2 overflow-x-auto pb-1"
          data-testid="activity-filters"
        >
          {filters.map((item) => {
            const selected =
              filter === item.id;

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onFilter?.(item.id);
                  onPage?.(1);
                }}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-2 text-xs font-black transition ${
                  selected
                    ? "border-[var(--do-accent)] bg-[var(--do-accent)] text-[var(--do-on-accent)]"
                    : "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-muted)] hover:border-[var(--do-accent)]"
                }`}
                aria-pressed={selected}
                data-testid={`activity-filter-${item.id}`}
              >
                {item.label}

                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                    selected
                      ? "bg-[var(--do-bg-elev)] text-[var(--do-on-accent)]"
                      : "bg-[var(--do-bg-2)] text-[var(--do-muted)]"
                  }`}
                >
                  {item.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {items.length === 0 ? (
        <div
          className="m-4 rounded-2xl border border-dashed border-[var(--do-border-strong)] bg-[var(--do-bg-2)]/50 p-8 text-center"
          data-testid="activity-empty-state"
        >
          <Clock
            size={26}
            weight="duotone"
            className="mx-auto text-[var(--do-accent)]"
          />

          <h3 className="mt-3 text-base font-black text-[var(--do-ink)]">
            {filter === "todo"
              ? "Aucune action à traiter"
              : "Aucune activité dans cette catégorie"}
          </h3>

          <p className="mt-1 text-sm text-[var(--do-muted)]">
            L’historique se remplira automatiquement au fil des échanges.
          </p>
        </div>
      ) : (
        <div
          className="relative px-4 sm:px-5"
          data-testid="unified-activity-timeline"
        >
          <div className="absolute bottom-5 left-[39px] top-5 w-px bg-[var(--do-border)] sm:left-[43px]" />

          {pagination.items.map(
            (item) =>
              renderRow(item)
          )}
        </div>
      )}

      <div className="px-4 pb-4 sm:px-5">
        <Pagination
          {...pagination}
          total={items.length}
          label="activités"
          onPage={onPage}
        />

        {technicalItems.length > 0 && (
          <div
            className="mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-2)]/45"
            data-testid="technical-history"
          >
            <button
              type="button"
              onClick={() =>
                setShowTechnical(
                  (current) => !current
                )
              }
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
              aria-expanded={showTechnical}
            >
              <span>
                <span className="block text-sm font-black text-[var(--do-ink)]">
                  Détails techniques
                </span>

                <span className="mt-0.5 block text-xs text-[var(--do-muted)]">
                  {technicalItems.length} événement(s) système secondaire(s)
                </span>
              </span>

              <span className="text-xs font-black text-[var(--do-accent)]">
                {showTechnical
                  ? "Masquer"
                  : "Voir les détails"}
              </span>
            </button>

            {showTechnical && (
              <div className="border-t border-[var(--do-border)] px-4 sm:px-5">
                {technicalItems.map(
                  (item) =>
                    renderRow(
                      item,
                      true
                    )
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}


/**
 * Notes commerciales.
 */
function NotesPanel({ notes, page, onPage, onAdd, onOpen, onDelete }) {
  const pagination = paginate(notes, page);

  return (
    <section className="au-card p-6" data-testid="prospect-notes-panel">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h2 className="label-eyebrow">Notes</h2>
        <button type="button" onClick={onAdd} className="inline-flex items-center gap-2 rounded-lg bg-[var(--do-accent-strong)] px-3 py-2 text-sm font-bold text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)]">
          <Plus size={15} /> Ajouter
        </button>
      </div>

      {notes.length === 0 ? (
        <EmptyState text="Aucune note enregistrée." />
      ) : (
        <div className="divide-y divide-[var(--do-border)]">
          {pagination.items.map((note) => (
            <ComplementaryInfoRow key={note.id} info={note} onOpen={() => onOpen(note)} onDelete={() => onDelete(note.id)} />
          ))}
        </div>
      )}

      <Pagination {...pagination} total={notes.length} label="notes" onPage={onPage} />
    </section>
  );
}

/**
 * Documents partagés.
 */
function DocumentsPanel({ documents, page, onPage, onOpen }) {
  const pagination = paginate(documents, page);

  return (
    <section className="au-card p-4 sm:p-5" data-testid="prospect-documents-panel">
      <h2 className="label-eyebrow mb-5">Documents partagés</h2>
      {documents.length === 0 ? (
        <EmptyState text="Aucun document partagé." />
      ) : (
        <div className="divide-y divide-[var(--do-border)]">
          {pagination.items.map((d, i) => {
            let label = "Actif";
            let cls = "bg-[var(--do-accent-soft)] text-[var(--do-accent)] border-[var(--do-border)]";
            if (d.revoked) {
              label = "Révoqué";
              cls = "bg-[var(--do-accent-soft)] text-[var(--do-accent)] border-[var(--do-border)]";
            } else if (d.expired) {
              label = "Expiré";
              cls = "bg-[var(--do-accent-soft)] text-[var(--do-accent)] border-[var(--do-border)]";
            }

            return (
              <div key={`${d.shareId}-${d.documentId}-${i}`} className="flex items-start justify-between gap-3 py-3.5">
                <button
                  type="button"
                  disabled={!d.sourceDocument}
                  onClick={() => onOpen?.(d)}
                  className="min-w-0 flex-1 text-left disabled:cursor-not-allowed"
                  aria-label={`Prévisualiser ${d.documentName || "le document"}`}
                  data-testid={`contact-document-preview-${d.documentId}`}
                >
                  <div className="text-sm font-semibold text-[var(--do-ink)] inline-flex items-center gap-2">
                    <FileText
                      size={17}
                      weight="duotone"
                      className="text-[var(--do-accent)]"
                    />
                    <span className="truncate">
                      {d.documentName || "Document partagé"}
                    </span>
                  </div>
                  <div className="text-xs text-[var(--do-muted)] mt-1 flex items-center gap-3 flex-wrap">
                    <span>{fmtDateTime(d.createdAt)}</span>
                    <span>par {d.createdByName}</span>
                    <span>{d.openCount} ouv.</span>
                    {d.lastOpenedAt && <span>dernière {relativeTime(d.lastOpenedAt)}</span>}
                    {!d.allowDownload && <span className="inline-flex items-center gap-1 text-[var(--do-accent)]"><Lock size={11} weight="bold" /> Lecture seule</span>}
                  </div>
                </button>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide border ${cls}`}>{label}</span>
              </div>
            );
          })}
        </div>
      )}
      <Pagination {...pagination} total={documents.length} label="documents" onPage={onPage} />
    </section>
  );
}

/**
 * Rendez-vous CRM internes du contact.
 *
 * Ces rendez-vous appartiennent au domaine
 * prospect_appointments et ne dépendent pas
 * d'un partage documentaire.
 */
function InternalAppointmentsPanel({
  appointments = [],
  loading = false,
  saving = false,
  onCreate,
  onEdit,
  onDelete,
}) {
  const statusLabels = {
    scheduled: "Planifié",
    completed: "Terminé",
    cancelled: "Annulé",
  };

  return (
    <section
      className="au-card p-4 sm:p-6"
      data-testid="prospect-internal-appointments-panel"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="label-eyebrow">
            Rendez-vous
          </div>

          <h2 className="mt-1 font-heading text-xl font-black text-[var(--do-ink)]">
            Rendez-vous Acta
          </h2>

          <p className="mt-1 text-sm text-[var(--do-muted)]">
            Rendez-vous planifiés directement depuis la fiche ou l’agenda commercial.
          </p>
        </div>

        <button
          type="button"
          onClick={onCreate}
          disabled={saving}
          className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-xl bg-[var(--do-accent)] px-4 py-2 text-sm font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)] disabled:opacity-50"
          data-testid="prospect-internal-appointment-create"
        >
          <Plus
            size={16}
            weight="bold"
          />
          Nouveau rendez-vous
        </button>
      </div>

      <div className="mt-5 space-y-3">
        {loading && (
          <div className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-sm text-[var(--do-muted)]">
            Chargement des rendez-vous…
          </div>
        )}

        {!loading &&
          appointments.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] p-6 text-center">
              <Calendar
                size={26}
                weight="duotone"
                className="mx-auto text-[var(--do-muted)]"
              />

              <div className="mt-2 text-sm font-black text-[var(--do-ink)]">
                Aucun rendez-vous planifié
              </div>

              <p className="mt-1 text-xs text-[var(--do-muted)]">
                Créez un rendez-vous sans générer de note automatique.
              </p>
            </div>
          )}

        {!loading &&
          appointments.map(
            (appointment) => {
              const editable =
                appointment.status ===
                "scheduled";

              return (
                <article
                  key={appointment.id}
                  className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4"
                  data-testid="prospect-internal-appointment-item"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="font-heading text-base font-black text-[var(--do-ink)]">
                        {appointment.subject ||
                          "Rendez-vous"}
                      </h3>

                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--do-muted)]">
                        <span>
                          {fmtDateTime(
                            appointment.startAt
                          )}
                        </span>

                        <span>
                          {appointment.durationMinutes ||
                            30} min
                        </span>

                        <span>
                          {appointment.meetingType ===
                          "physical"
                            ? "Physique"
                            : "En ligne"}
                        </span>

                        <span>
                          {statusLabels[
                            appointment.status
                          ] ||
                            appointment.status ||
                            "Planifié"}
                        </span>
                      </div>

                      {appointment.location && (
                        <p className="mt-2 text-xs text-[var(--do-muted)]">
                          {appointment.location}
                        </p>
                      )}

                      {appointment.description && (
                        <p className="mt-2 text-sm text-[var(--do-muted)]">
                          {appointment.description}
                        </p>
                      )}
                    </div>

                    {editable && (
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            onEdit?.(
                              appointment
                            )
                          }
                          disabled={saving}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-ink)] disabled:opacity-50"
                          data-testid="prospect-internal-appointment-edit"
                        >
                          <PencilSimple
                            size={14}
                            weight="bold"
                          />
                          Modifier
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            onDelete?.(
                              appointment
                            )
                          }
                          disabled={saving}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-accent)] disabled:opacity-50"
                          data-testid="prospect-internal-appointment-delete"
                        >
                          <Trash
                            size={14}
                            weight="bold"
                          />
                          Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                </article>
              );
            }
          )}
      </div>
    </section>
  );
}


/**
 * Messages par partage.
 */
function MessagesPanel({
  shares,
  selectedShareId,
  onSelectShare,
  messages,
  highlightedMessageId,
  reply,
  onReply,
  onSend,
}) {
  return (
    <section
      className="au-card p-4 sm:p-5"
      data-testid="prospect-messages-panel"
    >
      <ExchangeShareSelector shares={shares} selectedShareId={selectedShareId} onSelectShare={onSelectShare} />
      <div className="mt-4 overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-2)]/50">
        <div className="min-h-[220px] max-h-[420px] space-y-3 overflow-y-auto p-4">
          {messages.length === 0 ? (
            <EmptyState text="Aucun message sur ce partage." />
          ) : (
            messages.map((item) => {
              const isCommercial = item.senderType === "commercial";
              return (
                <div
                  key={item.id}
                  data-message-id={item.id}
                  className={`flex scroll-mt-24 transition-all duration-500 ${
                    isCommercial
                      ? "justify-end"
                      : "justify-start"
                  } ${
                    highlightedMessageId === item.id
                      ? "rounded-2xl bg-[var(--do-accent-soft)] p-2 ring-2 ring-[var(--do-accent)]"
                      : ""
                  }`}
                >
                  <div className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isCommercial
                      ? "bg-[var(--do-accent)] text-[var(--do-on-accent)]"
                      : "border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]"
                  }`}>
                    {item.content || item.message}
                    <div className={`text-xs mt-1 ${isCommercial ? "text-[var(--do-accent)] text-right" : "text-[var(--do-muted)]"}`}>
                      {item.authorName || (isCommercial ? "Vous" : "Prospect")} · {fmtDateTime(item.createdAt)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex gap-2 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3">
          <input
            value={reply}
            onChange={(e) => onReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSend();
            }}
            placeholder="Répondre au prospect..."
            className="flex-1 rounded-xl border border-[var(--do-border)] px-4 py-3 text-sm outline-none focus:border-[var(--do-border-strong)]"
            data-testid="commercial-message-input"
          />
          <button
            type="button"
            onClick={onSend}
            disabled={!reply.trim()}
            className="flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--do-accent)] text-[var(--do-on-accent)] transition hover:brightness-110 disabled:bg-[var(--do-bg-2)]"
            data-testid="commercial-message-send"
          >
            <PaperPlaneTilt size={20} weight="fill" />
          </button>
        </div>
      </div>
    </section>
  );
}

/**
 * Rendez-vous par partage.
 */
function MeetingsPanel({
  shares,
  selectedShareId,
  onSelectShare,
  meetings,
  slots,
  onAddSlot,
  onUpdateSlot,
  onRemoveSlot,
  onPropose,
  editingMeetingId,
  onEditMeeting,
  onDeleteMeeting,
  prospect,
  onSelectSlot,
  onConfirmAccepted,
  onRefuseProspect,
  onCounterProspect,
  counterMeetingId,
}) {
  // ACTA_SHARE_MEETING_COLLAPSIBLE_FORM_V2
  const [shareMeetingFormOpen, setShareMeetingFormOpen] = useState(false);

  if (!shares?.length) {
    return (
      <section
        className="au-card p-4 sm:p-6"
        data-testid="prospect-meetings-panel"
      >
        <div className="rounded-2xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 py-8 text-center">
          <Calendar
            size={28}
            weight="duotone"
            className="mx-auto text-[var(--do-muted)]"
          />

          <h2 className="mt-3 font-heading text-lg font-black text-[var(--do-ink)]">
            Aucun partage disponible
          </h2>

          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[var(--do-muted)]">
            Partagez d’abord un document avec ce contact avant de proposer un rendez-vous lié à un partage.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="au-card p-6" data-testid="prospect-meetings-panel">
      <ExchangeShareSelector shares={shares} selectedShareId={selectedShareId} onSelectShare={onSelectShare} />

      <div className="grid xl:grid-cols-[1fr_380px] gap-5 mt-5">
        <div className="space-y-4">
          {meetings.length === 0 ? (
            <EmptyState text="Aucun rendez-vous proposé sur ce partage." />
          ) : (
            meetings.map((meeting) => (
              <div key={meeting.id} className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-heading font-black text-[var(--do-ink)]">
                      {meeting.createdByType === "commercial" ? "Rendez-vous proposés par vous" : "Rendez-vous proposés par le prospect"}
                    </h3>
                    <p className="text-xs text-[var(--do-muted)] mt-1">
                      {fmtDateTime(meeting.createdAt)}
                      {" · "}
                      {meeting.status === "selected"
                        ? "Confirmé"
                        : meeting.status === "accepted"
                          ? "Accepté · à confirmer"
                          : meeting.status === "refused"
                            ? "Refusé"
                            : meeting.status ===
                                "counter_proposed"
                              ? "Contre-proposition envoyée"
                              : "En attente"}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-accent)]">
                      {meeting.slots?.length || 0} créneau(x)
                    </span>
                    {meeting.createdByType === "commercial" &&
                      meeting.status === "pending" && (
                      <>
                        <button type="button" onClick={() => onEditMeeting(meeting)} className="rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-1 text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-bg-solid)]" data-testid="meeting-edit-action">Modifier</button>
                        <button type="button" onClick={() => onDeleteMeeting(meeting)} className="rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-1 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]" data-testid="meeting-delete-action">Supprimer</button>
                      </>
                    )}
                  </div>
                </div>

                {/* ACTA_MEETING_INTERNAL_DECISION_ACTIONS_V1 */}
                {meeting.createdByType === "commercial" &&
                  meeting.status === "accepted" && (
                    <button
                      type="button"
                      onClick={() =>
                        onConfirmAccepted?.(meeting)
                      }
                      className="mt-4 w-full rounded-xl bg-[var(--do-accent)] px-4 py-3 text-sm font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)]"
                      data-testid="meeting-confirm-accepted-action"
                    >
                      Confirmer le rendez-vous
                    </button>
                  )}

                {meeting.createdByType === "prospect" &&
                  meeting.status === "pending" && (
                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() =>
                          onCounterProspect?.(meeting)
                        }
                        className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                        data-testid="meeting-counter-proposal-action"
                      >
                        Proposer un autre créneau
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          onRefuseProspect?.(meeting)
                        }
                        className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
                        data-testid="meeting-refuse-prospect-action"
                      >
                        Refuser la proposition
                      </button>
                    </div>
                  )}

                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  {(meeting.slots || []).map((slot, index) => {
                    const selected =
                      meeting.status === "selected" &&
                      meeting.selectedSlotIndex === index;
                    const calendarLinks = selected
                      ? buildMeetingCalendarLinks(
                          slot,
                          prospect
                        )
                      : null;
                    const invitationLinks = selected
                      ? buildMeetingInvitationShareLinks(
                          slot,
                          prospect
                        )
                      : null;
                    return (
                      <div key={`${meeting.id}-${index}`} className={`rounded-xl border p-3 ${selected ? "border-[var(--do-border)] bg-[var(--do-accent-soft)]" : "border-[var(--do-border)] bg-[var(--do-bg-solid)]"}`}>
                        <div className="text-sm font-bold text-[var(--do-ink)]">{slot.date}</div>
                        <div className="mt-1 text-xs text-[var(--do-muted)]">
                          {slot.start}
                          {" · "}
                          {slot.durationMinutes
                            ? `${slot.durationMinutes} min`
                            : slot.end
                              ? `jusqu’à ${slot.end}`
                              : "30 min"}
                        </div>

                        {slot.location && (
                          <div className="mt-2 break-words text-xs text-[var(--do-muted)]">
                            <span className="font-black">
                              {slot.locationType === "physical"
                                ? "Adresse : "
                                : "Lieu / connexion : "}
                            </span>
                            {slot.location}
                          </div>
                        )}

                          {selected ? (
                            <div
                              className="mt-3 space-y-3"
                              data-testid="meeting-selected-slot-calendar-links"
                            >
                              <div className="inline-flex rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-xs font-black text-[var(--do-ink)]">
                                Rendez-vous confirmé
                              </div>

                              {calendarLinks &&
                                invitationLinks && (
                                <>
                                  <div
                                    className="rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-3 text-xs font-bold leading-5 text-[var(--do-ink)]"
                                    data-testid="meeting-manual-calendar-handoff"
                                  >
                                    Rien n’est envoyé automatiquement.
                                    Choisissez Gmail ou Outlook, complétez
                                    si nécessaire les informations de
                                    visioconférence, puis envoyez
                                    manuellement l’invitation.
                                  </div>

                                  <div className="grid gap-2 sm:grid-cols-2">
                                    <a
                                      href={calendarLinks.outlook}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex min-h-[44px] items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-center text-xs font-black text-[var(--do-ink)] hover:bg-[var(--do-accent-soft)]"
                                      data-testid="meeting-open-outlook"
                                    >
                                      Ouvrir dans Outlook
                                    </a>

                                    <a
                                      href={invitationLinks.gmail}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex min-h-[44px] items-center justify-center rounded-xl bg-[var(--do-accent)] px-3 py-2 text-center text-xs font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)]"
                                      data-testid="meeting-open-gmail"
                                    >
                                      Ouvrir Gmail
                                    </a>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : meeting.createdByType ===
                              "prospect" &&
                            meeting.status === "pending" ? (
                            <button
                              type="button"
                              onClick={() =>
                                onSelectSlot?.(
                                  meeting,
                                  index
                                )
                              }
                              className="mt-3 flex min-h-[44px] w-full items-center justify-center rounded-xl bg-[var(--do-accent)] px-4 py-3 text-center text-sm font-black text-[var(--do-on-accent)] shadow-sm transition hover:bg-[var(--do-accent-strong)] focus:outline-none focus:ring-2 focus:ring-[var(--do-accent)]0 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[var(--do-bg-2)]"
                              data-testid="meeting-select-slot-action"
                            >
                              Valider ce créneau
                            </button>
                          ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="h-fit">
          {!shareMeetingFormOpen &&
          !editingMeetingId &&
          !counterMeetingId ? (
            <button type="button" onClick={() => setShareMeetingFormOpen(true)} className="min-h-[52px] w-full rounded-2xl bg-[var(--do-accent)] px-5 py-3 text-sm font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)]" data-testid="share-meeting-open-form">
              Proposer un rendez-vous
            </button>
          ) : (
            <div className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]" data-testid="share-meeting-form">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-heading font-black text-[var(--do-ink)]">
                    {counterMeetingId
                      ? "Proposer un autre créneau"
                      : editingMeetingId
                        ? "Modifier la proposition"
                        : "Proposer un rendez-vous"}
                  </h3>
                  <p className="mt-1 text-xs text-[var(--do-muted)]">Proposez un seul créneau associé au partage sélectionné.</p>
                </div>
                {!editingMeetingId &&
                !counterMeetingId && (
                  <button type="button" onClick={() => setShareMeetingFormOpen(false)} className="h-10 w-10 shrink-0 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-xl font-black text-[var(--do-muted)]" aria-label="Fermer le formulaire">×</button>
                )}
              </div>

              <div className="space-y-3 mt-4">
            {slots.map((slot, index) => (
              <div key={slot.id} className="rounded-2xl border border-[var(--do-border)] p-3 bg-[var(--do-bg-solid)]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-black text-[var(--do-ink)] uppercase">Rendez-vous {index + 1}</div>
                  {slots.length > 1 && (
                    <button type="button" onClick={() => onRemoveSlot(slot.id)} className="text-[var(--do-accent)]">
                      <Trash size={15} />
                    </button>
                  )}
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="space-y-1">
                    <span className="text-[11px] font-black text-[var(--do-muted)]">
                      Date
                    </span>
                    <input
                      type="date"
                      min={minimumMeetingInput(5).date}
                      value={slot.date}
                      onChange={(event) =>
                        onUpdateSlot(
                          slot.id,
                          "date",
                          event.target.value
                        )
                      }
                      className="prospect-mobile-datetime w-full min-w-0 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-base sm:text-sm"
                    />
                  </label>

                  <label className="space-y-1">
                    <span className="text-[11px] font-black text-[var(--do-muted)]">
                      Heure
                    </span>
                    <input
                      type="time"
                      min={slot.date === localDateInputValue() ? localTimeInputValue(minimumMeetingStart(5)) : undefined}
                      value={slot.start}
                      onChange={(event) =>
                        onUpdateSlot(
                          slot.id,
                          "start",
                          event.target.value
                        )
                      }
                      className="prospect-mobile-datetime w-full min-w-0 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-base sm:text-sm"
                    />
                  </label>

                  <label className="space-y-1">
                    <span className="text-[11px] font-black text-[var(--do-muted)]">
                      Durée
                    </span>
                    <select
                      value={slot.durationMinutes}
                      onChange={(event) =>
                        onUpdateSlot(
                          slot.id,
                          "durationMinutes",
                          event.target.value
                        )
                      }
                      className="w-full rounded-xl border border-[var(--do-border)] px-3 py-2 text-sm"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 heure</option>
                      <option value="90">1 h 30</option>
                      <option value="120">2 heures</option>
                    </select>
                  </label>

                  <label className="space-y-1">
                    <span className="text-[11px] font-black text-[var(--do-muted)]">
                      Type
                    </span>
                    <select
                      value={slot.locationType}
                      onChange={(event) =>
                        onUpdateSlot(
                          slot.id,
                          "locationType",
                          event.target.value
                        )
                      }
                      className="w-full rounded-xl border border-[var(--do-border)] px-3 py-2 text-sm"
                    >
                      <option value="online">En ligne</option>
                      <option value="physical">Physique</option>
                    </select>
                  </label>
                </div>

                {slot.locationType === "physical" ? (
                  <label className="mt-2 block space-y-1">
                    <span className="text-[11px] font-black text-[var(--do-muted)]">
                      Adresse du rendez-vous
                    </span>
                    <input
                      type="text"
                      value={slot.location}
                      onChange={(event) =>
                        onUpdateSlot(
                          slot.id,
                          "location",
                          event.target.value
                        )
                      }
                      placeholder="Adresse complète"
                      className="w-full rounded-xl border border-[var(--do-border)] px-3 py-2 text-sm"
                      required
                      data-testid="meeting-physical-address"
                    />
                  </label>
                ) : (
                  <div
                    className="mt-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs text-[var(--do-accent)]"
                    data-testid="meeting-online-no-link-required"
                  >
                    Le lien de visioconférence pourra être ajouté après confirmation.
                  </div>
                )}
              </div>
            ))}
          </div>

              <button type="button" onClick={onPropose} className="mt-5 min-h-[48px] w-full rounded-xl bg-[var(--do-accent)] py-3 text-sm font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)]">
                {counterMeetingId
                  ? "Envoyer la contre-proposition"
                  : editingMeetingId
                    ? "Enregistrer les modifications"
                    : "Envoyer la proposition"}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ACTA_EXCHANGE_SHARE_SELECTOR_LABEL_V1
function formatExchangeShareDate(value) {
  if (!value) return "Date inconnue";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "fr-FR",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  )
    .format(date)
    .replace(",", " à");
}

function exchangeShareDocumentLabel(share = {}) {
  const names = (
    share.exchangeDocumentNames || []
  ).filter(Boolean);

  if (names.length === 1) {
    return names[0];
  }

  if (names.length > 1) {
    return `${names[0]} + ${names.length - 1} autre${
      names.length > 2 ? "s" : ""
    }`;
  }

  return "Document partagé";
}

/**
 * Sélecteur de partage commun aux messages et rendez-vous.
 */
function ExchangeShareSelector({ shares, selectedShareId, onSelectShare }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">Partage concerné</div>
      {shares.length === 0 ? (
        <EmptyState text="Aucun partage pour ce prospect." />
      ) : (
        <select
          value={selectedShareId}
          onChange={(e) => onSelectShare(e.target.value)}
          className="w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm font-semibold outline-none focus:border-[var(--do-border-strong)]"
          data-testid="exchange-share-select"
        >
          {shares.map((share) => (
            <option key={share.id} value={share.id}>
              {formatExchangeShareDate(share.createdAt)} · {exchangeShareDocumentLabel(share)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Carte statistique.
 */
function Stat({ label, value, icon: Icon, small }) {
  return (
    <div className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
      <div className="flex items-center gap-1.5">
        <Icon size={14} weight="duotone" className="text-[var(--do-accent)]" />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className={`font-mono-stat font-bold ${small ? "text-sm" : "text-xl"} mt-2 text-[var(--do-ink)]`}>{value}</div>
    </div>
  );
}

/**
 * Pagination.
 */
function Pagination({ page, totalPages, start, end, total, label, onPage }) {
  if (total <= PAGE_SIZE) return null;

  return (
    <div className="mt-5 flex items-center justify-between gap-3 text-xs text-[var(--do-muted)]">
      <span>{start} à {end} sur {total} {label}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} className="px-2 py-1 border border-[var(--do-border)] rounded disabled:opacity-40">‹</button>
        <span className="px-2">Page {page}/{totalPages}</span>
        <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} className="px-2 py-1 border border-[var(--do-border)] rounded disabled:opacity-40">›</button>
      </div>
    </div>
  );
}

/**
 * Ligne de note.
 */
function ComplementaryInfoRow({ info, onOpen, onDelete }) {
  const text = info.content || "";
  const isLong = text.length > 260;

  return (
    <div className="py-4 flex items-start gap-4">
      <div className="h-9 w-9 rounded-full bg-[var(--do-accent-soft)] text-[var(--do-accent)] flex items-center justify-center font-bold shrink-0">
        {(text.trim()[0] || "I").toUpperCase()}
      </div>

      <div className="min-w-0 flex-1">
        <div className={`text-sm text-[var(--do-ink)] whitespace-pre-wrap leading-6 ${isLong ? "max-h-[112px] overflow-hidden" : ""}`}>
          {text}
        </div>
        {isLong && (
          <button onClick={onOpen} className="mt-2 text-xs font-semibold text-[var(--do-accent)] hover:underline">
            Ouvrir pour tout lire
          </button>
        )}
        <div className="text-xs text-[var(--do-muted)] mt-2">
          {fmtDateTime(info.createdAt || info.activityDate)} · {info.authorName || "—"}
        </div>
      </div>

      <button onClick={onOpen} className="px-2 py-1 text-xs border border-[var(--do-border)] rounded hover:bg-[var(--do-accent-soft)]">
        Lire
      </button>
      <button onClick={onDelete} className="p-1 hover:bg-[var(--do-accent-soft)] rounded text-[var(--do-accent)]">
        <Trash size={14} />
      </button>
    </div>
  );
}

/**
 * Modal réutilisable.
 */
function Modal({
  title,
  children,
  onClose,
  testid,
  closeOnBackdrop = true,
}) {
  return (
    <div
      className="acta-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
      onClick={
        closeOnBackdrop
          ? onClose
          : undefined
      }
      data-testid={testid}
    >
      <div className="acta-modal-surface acta-modal-scroll w-full max-w-3xl max-h-[90dvh] rounded-3xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title}>
        <div className="p-5 border-b border-[var(--do-border)] flex items-center justify-between">
          <h2 className="font-heading font-black text-lg">{title}</h2>
          <button onClick={onClose} className="text-[var(--do-muted)] hover:text-[var(--do-ink)]">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/**
 * Input formulaire.
 */
function Input({
  label,
  value,
  onChange,
  type = "text",
  min,
  max,
  step,
  maxLength,
  testid,
}) {
  return (
    <label className="block text-sm font-semibold text-[var(--do-ink)]">
      {label}

      <input
        type={type}
        value={
          value === null ||
          value === undefined
            ? ""
            : value
        }
        min={min}
        max={max}
        step={step}
        maxLength={maxLength}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        className={`mt-2 w-full min-w-0 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 outline-none focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)] ${
          [
            "date",
            "time",
            "datetime-local",
          ].includes(type)
            ? "prospect-mobile-datetime text-base sm:text-sm"
            : "text-sm"
        }`}
        data-testid={testid}
      />
    </label>
  );
}

/**
 * Textarea formulaire.
 */
function TextArea({ label, value, onChange, placeholder, max, testid }) {
  return (
    <label className="block text-sm font-semibold text-[var(--do-ink)]">
      {label}
      <textarea
        value={value}
        maxLength={max}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full min-h-[180px] rounded-lg border border-[var(--do-border)] px-3 py-2 text-sm outline-none focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
        data-testid={testid}
      />
      <span className="block text-right text-xs text-[var(--do-muted)] mt-1">{value.length} / {max}</span>
    </label>
  );
}

/**
 * État vide.
 */
function EmptyState({ text }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] p-6 text-sm text-[var(--do-muted)]">
      {text}
    </div>
  );
}
